import { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  selectDirectory, loadHandle, queryPermission, requestPermission,
  loadFreelancerDocs, saveFreelancerDocs, deleteRemovedFiles,
} from './FileStorage';
import api from './api';
import { useTasks } from './TasksContext';
import { TaskPanel } from './TasksPage';
import { useFilePreview, FilePreviewModal } from './FilePreview';

// ─── Brand ────────────────────────────────────────────────────────────────────
export const RED = 'var(--accent)';

// ─── Dictionaries ─────────────────────────────────────────────────────────────
export const DEPARTMENTS = ['LX', 'VX', 'AX', 'RX', 'PX', 'PM'];
export const DEPT_FULL   = { LX: 'Lighting', VX: 'Video', AX: 'Audio', RX: 'Rigging', PX: 'Power', PM: 'Project Mgmt' };
const BASE_SKILLS = ['Tech', 'Engineer', 'Operator', 'Crew Chief'];
const DEPT_SKILLS = {
  LX: [...BASE_SKILLS, 'LD'],
  VX: BASE_SKILLS,
  AX: [...BASE_SKILLS, 'Conference systems'],
  RX: BASE_SKILLS,
  PX: BASE_SKILLS,
  PM: BASE_SKILLS,
};
const OTHER_BASE_SKILLS = ['Vectorworks', 'Autocad'];
const LANG_LEVELS     = ['Native', 'Fluent', 'Basic', 'Enough for work']; // узгоджено з онбордингом фрілансера
const DEPT_LEVELS     = ['Crew chief', 'Technician', 'Engineer', 'Operator'];
const BANK_CURRENCIES = ['AED', 'EUR', 'USD', 'UAH', 'ZAR', 'SAR', 'KWD'];
const CURRENCIES_LIST = ['EUR', 'USD', 'GBP', 'AED', 'SAR', 'QAR', 'UAH'];
const GENDERS         = ['Male', 'Female', 'Other'];
const MARITAL         = ['Single', 'Married', 'Divorced', 'Widowed'];
const STATUS_LABEL    = { active: 'Active', pending: 'Pending', blocked: 'Blocked' };
const STATUS_COLOR    = {
  active:  { bg: 'var(--success-bg)', color: 'var(--success-text)', border: 'var(--success-text)' },
  pending: { bg: 'var(--warning-bg)', color: 'var(--warning-text)', border: 'var(--warning-text)' },
  blocked: { bg: 'var(--danger-bg)', color: 'var(--accent)', border: 'var(--accent)' },
};
export const DEPT_COLOR  = { LX: 'var(--warning-text)', VX: 'var(--info-text)', AX: 'var(--success-text)', RX: 'var(--purple-text)', PX: '#f97316', PM: 'var(--accent)' };
const PORTAL_ROLES = ['Freelancer', 'HR', 'Finance', 'PM', 'Admin'];

// ─── All table columns ────────────────────────────────────────────────────────
// Показані/приховані колонки та їх порядок зберігаються в localStorage (cp-freelancer-cols),
// тому вибір адміна не скидається між сесіями.
const ALL_COLUMNS = [
  { key: 'name',        label: 'Name' },
  { key: 'departments', label: 'Departments' },
  { key: 'skills',      label: 'Skills' },
  { key: 'status',      label: 'Status' },
  { key: 'country',     label: 'Country' },
  { key: 'address',     label: 'Address' },
  { key: 'email',       label: 'Email' },
  { key: 'phone',       label: 'Phone' },
  { key: 'whatsapp',    label: 'WhatsApp' },
  { key: 'additionalPhone', label: 'Additional Phone' },
  { key: 'travelPort',  label: 'Travel Port' },
  { key: 'languages',   label: 'Languages' },
  { key: 'visas',       label: 'Visas' },
  { key: 'projects',    label: 'Projects' },
  { key: 'registered',  label: 'Registered' },
  { key: 'emergencyContact', label: 'Emergency Contact' },
  { key: 'bankAccounts',     label: 'Bank Accounts' },
  { key: 'currency',         label: 'Currency' },
  { key: 'passportNumber',   label: 'Passport №' },
  { key: 'passportExpiry',   label: 'Passport Expiry' },
  { key: 'dob',               label: 'Date of Birth' },
  { key: 'gender',            label: 'Gender' },
  { key: 'nationality',       label: 'Nationality' },
  { key: 'maritalStatus',     label: 'Marital Status' },
  { key: 'birthPlace',        label: 'Place of Birth' },
  { key: 'driveLink',         label: 'Drive Link' },
  { key: 'onboarding',        label: 'Onboarding' },
  { key: 'comment',           label: 'Admin Comment' },
  { key: 'freelancerComment', label: 'Freelancer Comment' },
];
const DEFAULT_COLS = new Set(['name','departments','skills','status','onboarding','country','email','travelPort','registered']);

// ── Column visibility persistence ─────────────────────────────────────────────
const COLS_STORAGE_KEY = 'cp-freelancer-cols';
const ALL_COL_KEYS = new Set(ALL_COLUMNS.map(c => c.key));
function loadVisibleCols() {
  try {
    const raw = localStorage.getItem(COLS_STORAGE_KEY);
    if (!raw) return DEFAULT_COLS;
    const arr = JSON.parse(raw).filter(k => ALL_COL_KEYS.has(k));
    return arr.length ? new Set(arr) : DEFAULT_COLS;
  } catch {
    return DEFAULT_COLS;
  }
}

// ─── Empty template ───────────────────────────────────────────────────────────
export const EMPTY_FREELANCER = {
  id: null, firstName: '', middleName: '', lastName: '', name: '', role: 'Freelancer', status: 'active',
  gender: '', dob: '', nationality: '', passportNumber: '', passportExpiry: '', maritalStatus: '',
  birthCountry: '', birthCity: '',
  additionalPassports: [],
  departments: [],
  departmentLevels: {},
  skills: [],
  customSkills: { LX: [], VX: [], AX: [], RX: [], PX: [], PM: [], Other: [] },
  languages: [],
  languagesDetail: [],
  email: '', phone: '', whatsapp: '', additionalPhone: '', travelPort: '',
  travelPortDetail: { country: '', city: '', code: '' },
  address: { zip: '', country: '', city: '', street: '' },
  emergencyContact: { phone: '', email: '' },
  driveLink: '',
  passportFiles: [],
  photoFiles: [],
  cvFiles: [],
  visas: [],
  otherDocs: [],
  finance: { bankName: '', accountNumber: '', swift: '', currency: 'EUR' },
  bankAccounts: [],
  projectIds: [],
  projects: [],
  comment: '',
  freelancerComment: '',
  registered: new Date().toISOString().slice(0, 10),
};

// ─── Mock data (exported for Dashboard) ──────────────────────────────────────
export const MOCK_FREELANCERS = [];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function initials(name) { return (name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2); }

export function Avatar({ name, size = 36 }) {
  const colors = ['var(--accent)','#2563eb','#16a34a','#ca8a04','#7c3aed','#0891b2'];
  const color  = name ? colors[name.charCodeAt(0) % colors.length] : 'var(--text-dim)';
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-primary)', fontSize: size * 0.36, fontWeight: 700, flexShrink: 0 }}>
      {initials(name)}
    </div>
  );
}

export function StatusBadge({ status }) {
  const s = STATUS_COLOR[status] || { bg: 'var(--bg-subtle)', color: 'var(--text-muted)', border: 'var(--text-disabled)' };
  return (
    <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: 700 }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

// Єдиний формат дат для відображення в UI: "27 Jul 2026".
// НЕ використовувати для <input type="date"> (там потрібен yyyy-mm-dd) чи CSV-експорту.
const DATE_FMT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
export function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return `${String(d.getDate()).padStart(2, '0')} ${DATE_FMT_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

export function docExpiryStatus(expiry) {
  if (!expiry) return null;
  const now  = new Date(); now.setHours(0,0,0,0);
  const exp  = new Date(expiry);
  const days = Math.ceil((exp - now) / 86400000);
  if (days < 0)   return { label: 'Expired',       color: 'var(--accent)', bg: 'var(--danger-bg)', days };
  if (days <= 30)  return { label: `${days}d left`, color: 'var(--accent)', bg: 'var(--danger-bg)', days };
  if (days <= 90)  return { label: `${days}d left`, color: 'var(--warning-text)', bg: 'var(--warning-bg)', days };
  return { label: 'Valid', color: 'var(--success-text)', bg: 'var(--success-bg)', days };
}

export function ExpiryBadge({ expiry }) {
  const s = docExpiryStatus(expiry);
  if (!s) return null;
  return <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.color}`, borderRadius: '4px', padding: '1px 6px', fontSize: '11px', fontWeight: 700, marginLeft: '6px' }}>{s.label}</span>;
}

// ─── CSV Export ───────────────────────────────────────────────────────────────
function exportToCSV(freelancers) {
  const rows = freelancers.map(f => ({
    'Name': f.name, 'Status': STATUS_LABEL[f.status] || f.status, 'Portal Role': f.role,
    'Gender': f.gender, 'Date of Birth': f.dob, 'Nationality': f.nationality,
    'Passport №': f.passportNumber, 'Passport Expiry': f.passportExpiry,
    'Marital Status': f.maritalStatus, 'Birth Country': f.birthCountry, 'Birth City': f.birthCity,
    'Departments': f.departments.join(', '),
    'Skills': f.skills.join(', '),
    'Custom Skills': Object.entries(f.customSkills || {}).filter(([,v]) => v.length).map(([k,v]) => `${k}: ${v.join(', ')}`).join(' | '),
    'Languages': f.languages.map(l => `${l.lang} (${l.level})`).join(', '),
    'Email': f.email, 'Phone': f.phone, 'WhatsApp': f.whatsapp,
    'Additional Phone': f.additionalPhone, 'Travel Port': f.travelPort,
    'ZIP': f.address?.zip, 'Country': f.address?.country, 'City': f.address?.city, 'Address': f.address?.street,
    'Drive Link': f.driveLink,
    'Visas': f.visas?.map(v => `${v.country} until ${v.expiry}`).join(', '),
    'Documents': f.documents?.map(d => `${d.type} ${d.number} until ${d.expiry}`).join(', '),
    'Bank Name': f.finance?.bankName, 'Account/IBAN': f.finance?.accountNumber,
    'SWIFT': f.finance?.swift, 'Currency': f.finance?.currency,
    'Comment': f.comment, 'Registered': f.registered,
  }));
  const headers = Object.keys(rows[0]);
  const csv = [headers.join(','), ...rows.map(r => headers.map(h => `"${String(r[h]||'').replace(/"/g,'""')}"`).join(','))].join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a'); a.href = url; a.download = `freelancers_${new Date().toISOString().slice(0,10)}.csv`; a.click();
  URL.revokeObjectURL(url);
}

// ─── File upload helpers ──────────────────────────────────────────────────────
function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Flag Document Modal ────────────────────────────────────────────────────
// Причини за замовчуванням — типові проблеми, через які документи
// відхиляють (обрізаний паспорт, розмите фото тощо), плюс вільний текст.
const FLAG_REASONS = [
  'Blurry / unreadable',
  'Cropped / incomplete',
  'Wrong document type',
  'Expired',
  "Doesn't match name on file",
  'Does not meet requirements',
];

// flag_reason у БД лишається одним TEXT-полем (без зміни схеми) — кілька
// обраних причин просто зʼєднуються через "; ". parseReasonString розбирає
// це назад на {chips, custom}, щоб при повторному відкритті модалки бачити,
// які саме чіпи були обрані раніше (частини, що не збігаються з жодним
// відомим чіпом, трактуються як вільний текст).
function parseReasonString(text) {
  if (!text) return { chips: [], custom: '' };
  const parts = text.split(';').map(s => s.trim()).filter(Boolean);
  const chips  = FLAG_REASONS.filter(r => parts.includes(r));
  const custom = parts.filter(p => !FLAG_REASONS.includes(p)).join('; ');
  return { chips, custom };
}

function FlagDocumentModal({ file, onConfirm, onCancel }) {
  const initial = parseReasonString(file.flagReason || '');
  const [chips, setChips]   = useState(initial.chips);
  const [custom, setCustom] = useState(initial.custom);
  const [busy, setBusy]     = useState(false);
  const alreadyFlagged = Boolean(file.flaggedAt);

  const toggleChip = r => setChips(cs => cs.includes(r) ? cs.filter(x => x !== r) : [...cs, r]);
  const combined = () => [...chips, ...(custom.trim() ? [custom.trim()] : [])].join('; ');

  const submit = async r => {
    setBusy(true);
    try { await onConfirm(r); }
    catch (e) { alert('Failed to update flag: ' + e.message); }
    finally { setBusy(false); }
  };

  const finalReason = combined();

  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: '12px', padding: '28px', maxWidth: '420px', width: '90%', fontFamily: 'Arial, sans-serif' }}>
        <div style={{ fontSize: '28px', marginBottom: '8px', textAlign: 'center' }}>🚩</div>
        <h3 style={{ color: 'var(--text-primary)', margin: '0 0 6px', fontSize: '16px', textAlign: 'center' }}>Flag "{file.name}"</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: '12px', margin: '0 0 16px', textAlign: 'center', lineHeight: 1.5 }}>
          The freelancer will see these reasons when you click "Notify" — pick one or more, and/or add your own.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
          {FLAG_REASONS.map(r => {
            const active = chips.includes(r);
            return (
              <button key={r} type="button" onClick={() => toggleChip(r)}
                style={{
                  padding: '5px 10px', borderRadius: '99px', fontSize: '11px', cursor: 'pointer',
                  border: `1px solid ${active ? 'var(--accent)' : 'var(--border-strong)'}`,
                  background: active ? 'var(--danger-bg)' : 'transparent',
                  color: active ? 'var(--accent)' : 'var(--text-secondary)',
                  fontWeight: active ? 700 : 400,
                }}>
                {active ? '✓ ' : ''}{r}
              </button>
            );
          })}
        </div>
        <textarea value={custom} onChange={e => setCustom(e.target.value)} placeholder="Additional details (optional)…" rows={3}
          style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg-input)', border: '1px solid var(--border-strong)', borderRadius: '6px', color: 'var(--text-body)', padding: '8px 10px', fontSize: '13px', resize: 'vertical', marginBottom: '16px', fontFamily: 'inherit' }} />
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button type="button" onClick={onCancel} disabled={busy}
            style={{ background: 'transparent', border: '1px solid var(--text-disabled)', color: 'var(--text-secondary)', padding: '9px 20px', borderRadius: '8px', cursor: busy ? 'default' : 'pointer', fontSize: '13px' }}>
            Cancel
          </button>
          {alreadyFlagged && (
            <button type="button" onClick={() => submit('')} disabled={busy}
              style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', padding: '9px 20px', borderRadius: '8px', cursor: busy ? 'default' : 'pointer', fontSize: '13px' }}>
              Remove flag
            </button>
          )}
          <button type="button" onClick={() => submit(finalReason)} disabled={busy || !finalReason}
            style={{ background: RED, border: 'none', color: 'var(--text-primary)', padding: '9px 20px', borderRadius: '8px', cursor: (busy || !finalReason) ? 'default' : 'pointer', fontSize: '13px', fontWeight: 'bold', opacity: (busy || !finalReason) ? 0.6 : 1 }}>
            {busy ? 'Saving…' : alreadyFlagged ? 'Update flag' : 'Flag document'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Реальний файл, завантажений на сервер (self-service фрілансера або адміном) —
// на відміну від FileChip/UploadArea нижче, які працюють з локальною File System
// Access API. Посилання веде на бекенд /api/freelancers/:id/documents/:docId/download.
// onFlag (опційно, лише для адмінських контекстів) — (reason) => Promise, щоб
// позначити/зняти позначку "не відповідає вимогам" саме на цьому документі.
export function ServerFileLink({ freelancerId, file, onRemove, onFlag }) {
  const isImage = file.mime?.startsWith('image/');
  const isPdf   = file.mime === 'application/pdf';
  const icon    = isImage ? '🖼' : isPdf ? '📄' : '📎';
  const { previewFile, openPreview, closePreview } = useFilePreview();
  const [flagOpen, setFlagOpen] = useState(false);
  const flagged = Boolean(file.flaggedAt);
  return (
    <div style={{ marginBottom: '4px' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0,
        background: flagged ? 'var(--danger-bg)' : 'var(--bg-deep)',
        border: `1px solid ${flagged ? 'var(--accent)' : 'var(--border)'}`,
        borderRadius: '6px', padding: '6px 10px',
      }}>
        <span style={{ fontSize: '14px', flexShrink: 0 }}>{icon}</span>
        <span
          onClick={() => openPreview({
            url: api.freelancers.documentUrl(freelancerId, file.id),
            downloadUrl: api.freelancers.documentUrl(freelancerId, file.id, { download: true }),
            name: file.name,
            mime: file.mime,
          })}
          style={{ color: 'var(--info-text)', fontSize: '12px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}>
          {file.name}
        </span>
        <span style={{ color: 'var(--text-disabled)', fontSize: '11px', flexShrink: 0 }}>{formatSize(file.size)}</span>
        {onFlag && (
          <button type="button" onClick={() => setFlagOpen(true)} title={flagged ? `Flagged: ${file.flagReason}` : 'Flag as not meeting requirements'}
            style={{ background: 'none', border: 'none', color: flagged ? 'var(--accent)' : 'var(--text-dim)', cursor: 'pointer', fontSize: '14px', padding: 0, lineHeight: 1, flexShrink: 0 }}>🚩</button>
        )}
        {onRemove && (
          <button type="button" onClick={onRemove}
            style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: '15px', padding: 0, lineHeight: 1, flexShrink: 0 }}>✕</button>
        )}
      </div>
      {flagged && (
        <div style={{ color: 'var(--danger-text-soft)', fontSize: '11px', padding: '3px 10px 0' }}>
          🚩 {file.flagReason}
        </div>
      )}
      <FilePreviewModal file={previewFile} onClose={closePreview} />
      {flagOpen && (
        <FlagDocumentModal
          file={file}
          onConfirm={async reason => { await onFlag(reason); setFlagOpen(false); }}
          onCancel={() => setFlagOpen(false)}
        />
      )}
    </div>
  );
}

function FileChip({ file, onRemove }) {
  const isImage = file.type?.startsWith('image/');
  const isPdf   = file.type === 'application/pdf';
  const icon    = isImage ? '🖼' : isPdf ? '📄' : '📎';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--bg-deep)', border: '1px solid var(--border)', borderRadius: '6px', padding: '6px 10px', minWidth: 0 }}>
      <span style={{ fontSize: '14px', flexShrink: 0 }}>{icon}</span>
      <a href={file.url} target="_blank" rel="noreferrer"
        style={{ color: 'var(--info-text)', fontSize: '12px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: 'none' }}>
        {file.name}
      </a>
      <span style={{ color: 'var(--text-disabled)', fontSize: '11px', flexShrink: 0 }}>{formatSize(file.size)}</span>
      {onRemove && (
        <button type="button" onClick={onRemove}
          style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: '15px', padding: 0, lineHeight: 1, flexShrink: 0 }}>✕</button>
      )}
    </div>
  );
}

function FileList({ files, onRemove }) {
  if (!files?.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '8px' }}>
      {files.map(file => (
        <FileChip key={file.id} file={file} onRemove={onRemove ? () => onRemove(file.id) : null} />
      ))}
    </div>
  );
}

function UploadArea({ onFiles, multiple = true, accept }) {
  const ref        = useRef(null);
  const [drag, setDrag] = useState(false);

  const handle = fileList => {
    const arr = Array.from(fileList).map(file => ({
      id:   `${Date.now()}-${Math.random()}`,
      name: file.name,
      size: file.size,
      type: file.type,
      url:  URL.createObjectURL(file),
      _raw: file,   // raw File object — used to write to disk
    }));
    if (arr.length) onFiles(arr);
  };

  return (
    <div
      onClick={() => ref.current?.click()}
      onDragOver={e => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={e => { e.preventDefault(); setDrag(false); handle(e.dataTransfer.files); }}
      style={{
        border: `2px dashed ${drag ? RED : 'var(--border)'}`, borderRadius: '8px',
        padding: '12px 16px', textAlign: 'center', cursor: 'pointer',
        background: drag ? '#1a0000' : 'var(--bg-deep)', transition: 'all 0.15s',
      }}>
      <div style={{ color: 'var(--text-dim)', fontSize: '13px' }}>📎 Click or drag file{multiple ? 's' : ''} here</div>
      <input ref={ref} type="file" multiple={multiple} accept={accept} style={{ display: 'none' }}
        onChange={e => { handle(e.target.files); e.target.value = ''; }} />
    </div>
  );
}

function DocSection({ title, required, children }) {
  return (
    <div style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: '10px', padding: '14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
        <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-body)' }}>{title}</span>
        {required && (
          <span style={{ background: 'var(--danger-bg)', color: 'var(--accent)', border: '1px solid var(--accent)', borderRadius: '4px', padding: '1px 6px', fontSize: '10px', fontWeight: 700 }}>Required</span>
        )}
      </div>
      {children}
    </div>
  );
}

// Посилання на картку фрілансера в новій вкладці. Токен передається в URL, а не
// через sessionStorage (яка не гарантовано клонується в нову вкладку, особливо з
// rel="noreferrer") — App.js підхоплює його на старті нової вкладки й одразу
// прибирає з адресного рядка, тож фрілансеру не доводиться логінитись знову.
function freelancerTabHref(id) {
  return `#freelancer/${id}?t=${encodeURIComponent(api.getToken() || '')}`;
}

// Реєстрація вважається незавершеною, поки фрілансер не пройшов усі три
// обов'язкові гейти: гайд → згода на обробку даних → онбординг-форма.
export function isRegistrationIncomplete(f) {
  return !f.guideAckAt || !f.consentSignedAt || !f.onboardingComplete;
}

// Чи є в цього фрілансера хоч один документ, позначений "не відповідає
// вимогам" (🚩), який ще не виправлено. Використовується і для бейджа в
// адмінській таблиці, і щоб показати/сховати кнопку "🔔 Notify".
export function flaggedDocuments(f) {
  const all = [
    ...(f.passportFiles || []), ...(f.photoFiles || []), ...(f.cvFiles || []),
    ...(f.otherDocs || []).flatMap(d => d.files || []),
  ];
  return all.filter(x => x.flaggedAt);
}

// ─── Row Actions Menu (portal) ────────────────────────────────────────────────
// Approve/Block/Edit/Delete — тільки для isAdmin (сервер все одно захищає ці
// ендпоінти через requireRole, але в UI вони теж не мали ховатись від не-адмінів).
// "Open in new tab" доступний завжди — це просто навігація на перегляд картки.
function RowActionsMenu({ freelancer, isAdmin, onApprove, onBlock, onEdit, onDelete, onRemind, onTask, onInvite }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos]   = useState({ top: 0, left: 0 });
  const btnRef          = useRef(null);

  const handleOpen = e => {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.right });
    }
    setOpen(p => !p);
  };

  const itemStyle = { padding: '9px 14px', cursor: 'pointer', borderRadius: '4px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none' };
  const item = (label, icon, color, onClick) => (
    <div
      onClick={e => { e.stopPropagation(); onClick(); setOpen(false); }}
      style={{ ...itemStyle, color: color || 'var(--text-body)' }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--border)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
      <span>{icon}</span>{label}
    </div>
  );

  return (
    <div style={{ display: 'inline-block' }} onClick={e => e.stopPropagation()}>
      <button
        ref={btnRef}
        onClick={handleOpen}
        style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-dim)', padding: '4px 8px', borderRadius: '6px', fontSize: '14px', cursor: 'pointer', lineHeight: 1 }}
        title="Actions">
        ⋮
      </button>
      {open && ReactDOM.createPortal(
        <>
          <div onClick={e => { e.stopPropagation(); setOpen(false); }} style={{ position: 'fixed', inset: 0, zIndex: 990 }} />
          <div style={{
            position: 'fixed', top: pos.top, left: pos.left, transform: 'translateX(-100%)',
            background: 'var(--bg-menu)', border: '1px solid var(--border-strong)', borderRadius: '8px',
            padding: '4px', zIndex: 1000, boxShadow: '0 8px 24px rgba(0,0,0,0.6)', minWidth: '190px',
          }}>
            <a href={freelancerTabHref(freelancer.id)} target="_blank" rel="noreferrer"
              onClick={e => { e.stopPropagation(); setOpen(false); }}
              style={{ ...itemStyle, color: 'var(--info-text)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--border)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <span>↗</span>Open in new tab
            </a>
            {isAdmin && (
              <>
                <div style={{ height: '1px', background: 'var(--border)', margin: '4px 0' }} />
                {freelancer.status === 'pending' && item('Approve',  '✓', 'var(--success-text)', onApprove)}
                {freelancer.status === 'active'  && item('Block',    '⊘', 'var(--warning-text)', onBlock)}
                {freelancer.status === 'blocked' && item('Unblock',  '↺', 'var(--info-text)', onApprove)}
                {freelancer.status === 'active' && isRegistrationIncomplete(freelancer) &&
                  item('Remind', '🔔', 'var(--info-text)', onRemind)}
                {!freelancer.userId &&
                  item(freelancer.invitedAt ? 'Resend invite' : 'Invite to portal', '✉️', 'var(--info-text)', onInvite)}
                {freelancer.userId && item('Task', '📌', 'var(--text-body)', onTask)}
                <div style={{ height: '1px', background: 'var(--border)', margin: '4px 0' }} />
                {item('Edit',   '✏️', 'var(--text-body)', onEdit)}
                <div style={{ height: '1px', background: 'var(--border)', margin: '4px 0' }} />
                {item('Delete', '🗑',  'var(--accent)', onDelete)}
              </>
            )}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────
function DeleteModal({ freelancer, onConfirm, onCancel }) {
  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: '12px', padding: '32px', maxWidth: '400px', width: '90%', fontFamily: 'Arial, sans-serif', textAlign: 'center' }}>
        <div style={{ fontSize: '32px', marginBottom: '12px' }}>🗑</div>
        <h3 style={{ color: 'var(--text-primary)', margin: '0 0 8px', fontSize: '18px' }}>Delete freelancer?</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: '0 0 24px', lineHeight: 1.5 }}>
          Permanently delete <span style={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>"{freelancer.name}"</span>?<br />
          This action cannot be undone. Their login account is removed too, and they'll get an email about it.
        </p>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
          <button onClick={onCancel}  style={{ background: 'transparent', border: '1px solid var(--text-disabled)', color: 'var(--text-secondary)', padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
          <button onClick={onConfirm} style={{ background: RED, border: 'none', color: 'var(--text-primary)', padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}>Delete</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function RemindAllModal({ count, busy, onConfirm, onCancel }) {
  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: '12px', padding: '32px', maxWidth: '420px', width: '90%', fontFamily: 'Arial, sans-serif', textAlign: 'center' }}>
        <div style={{ fontSize: '32px', marginBottom: '12px' }}>🔔</div>
        <h3 style={{ color: 'var(--text-primary)', margin: '0 0 8px', fontSize: '18px' }}>Remind everyone?</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: '0 0 24px', lineHeight: 1.5 }}>
          Send a reminder email to <span style={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>{count} freelancer{count === 1 ? '' : 's'}</span> with incomplete onboarding.<br />
          Anyone reminded in the last 3 days is skipped automatically.
        </p>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
          <button onClick={onCancel} disabled={busy}
            style={{ background: 'transparent', border: '1px solid var(--text-disabled)', color: 'var(--text-secondary)', padding: '10px 24px', borderRadius: '8px', cursor: busy ? 'default' : 'pointer', fontSize: '14px' }}>
            Cancel
          </button>
          <button onClick={onConfirm} disabled={busy}
            style={{ background: 'var(--info-text)', border: 'none', color: 'var(--on-accent)', padding: '10px 24px', borderRadius: '8px', cursor: busy ? 'default' : 'pointer', fontSize: '14px', fontWeight: 'bold', opacity: busy ? 0.7 : 1 }}>
            {busy ? 'Sending…' : 'Send reminders'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function FreelancersPage({ currentUser, onBack, freelancers: extFreelancers, setFreelancers: extSetFreelancers, allUsers = [], projects = [] }) {
  const [intFreelancers, setIntFreelancers] = useState(MOCK_FREELANCERS);
  const freelancers    = extFreelancers    ?? intFreelancers;
  const setFreelancers = extSetFreelancers ?? setIntFreelancers;
  const { addTask }    = useTasks();
  const [taskFor,       setTaskFor]         = useState(null); // фрілансер, якому зараз створюємо Task

  const [view,           setView]           = useState('table');
  const [search,         setSearch]         = useState('');
  const [statusFilter,   setStatusFilter]   = useState('All');
  const [deptFilter,     setDeptFilter]     = useState('All');
  const [countryFilter,  setCountryFilter]  = useState('All');
  const [langFilter,     setLangFilter]     = useState('All');
  const [skillFilter,    setSkillFilter]    = useState('All');
  const [travelFilter,   setTravelFilter]   = useState('All');
  const [docFilter,      setDocFilter]      = useState('All');
  const [showFilters,    setShowFilters]    = useState(false);
  const [selected,       setSelected]       = useState(null);
  const [editModal,      setEditModal]      = useState(null);
  const [confirmDel,     setConfirmDel]     = useState(null);
  const [confirmRemindAll, setConfirmRemindAll] = useState(false); // модалка "Remind everyone"
  const [remindAllBusy,    setRemindAllBusy]    = useState(false);
  const [checkedIds,     setCheckedIds]     = useState(new Set());
  const [visibleCols,    setVisibleCols]    = useState(loadVisibleCols);
  const [showColPicker,  setShowColPicker]  = useState(false);

  // ── File storage ─────────────────────────────────────────────────────────
  const [storageDir,    setStorageDir]    = useState(null);
  const [storageName,   setStorageName]   = useState('');
  // 'init' | 'none' | 'connected' | 'disconnected' | 'unsupported'
  const [storageStatus, setStorageStatus] = useState('init');

  useEffect(() => {
    if (!window.showDirectoryPicker) { setStorageStatus('unsupported'); return; }
    loadHandle().then(async h => {
      if (!h) { setStorageStatus('none'); return; }
      setStorageDir(h);
      setStorageName(h.name);
      const perm = await queryPermission(h);
      setStorageStatus(perm === 'granted' ? 'connected' : 'disconnected');
    }).catch(() => setStorageStatus('none'));
  }, []);

  const connectStorage = async () => {
    const h = await selectDirectory();
    if (!h) return;
    setStorageDir(h);
    setStorageName(h.name);
    setStorageStatus('connected');
  };

  const reconnectStorage = async () => {
    if (!storageDir) return;
    const ok = await requestPermission(storageDir);
    if (ok) setStorageStatus('connected');
  };

  const isAdmin    = !currentUser || ['admin', 'Admin', 'SuperAdmin'].includes(currentUser?.role);
  const canFinance = isAdmin || currentUser?.role === 'Finance';

  const counts = { active: 0, pending: 0, blocked: 0 };
  freelancers.forEach(f => { if (f.status in counts) counts[f.status]++; });
  const onboardingIncompleteCount = freelancers.filter(f => f.status === 'active' && isRegistrationIncomplete(f)).length;

  const allCountries = ['All', ...[...new Set(freelancers.map(f => f.address?.country).filter(Boolean))].sort()];
  const allLanguages = ['All', ...[...new Set(freelancers.flatMap(f => (f.languages||[]).map(l => l.lang)).filter(Boolean))].sort()];
  const allSkills    = ['All', ...[...new Set(freelancers.flatMap(f => f.skills||[]))].sort()];

  const activeFilterCount = [countryFilter, langFilter, skillFilter, travelFilter, docFilter].filter(v => v !== 'All').length;

  const filtered = freelancers.filter(f => {
    const q = (search || '').toLowerCase();
    const matchSearch = !q || [f.name, f.email, f.phone, f.address?.country, f.travelPort, f.nationality, ...(f.projects||[])].some(v => v?.toLowerCase().includes(q));
    const matchStatus  = statusFilter  === 'All' || f.status === statusFilter;
    const matchDept    = deptFilter    === 'All' || f.departments?.includes(deptFilter);
    const matchCountry = countryFilter === 'All' || f.address?.country === countryFilter;
    const matchLang    = langFilter    === 'All' || (f.languages||[]).some(l => l.lang === langFilter);
    const matchSkill   = skillFilter   === 'All' || (f.skills||[]).includes(skillFilter);
    const matchTravel  = travelFilter  === 'All'
      || (travelFilter === 'yes' && !!f.travelPort)
      || (travelFilter === 'no'  && !f.travelPort);
    const matchDoc = docFilter === 'All' || (() => {
      const all = [f.passportExpiry, ...(f.documents||[]).map(d => d.expiry), ...(f.visas||[]).map(v => v.expiry)].filter(Boolean);
      if (docFilter === 'expired')  return all.some(e => docExpiryStatus(e)?.days < 0);
      if (docFilter === 'expiring') return all.some(e => { const s = docExpiryStatus(e); return s && s.days >= 0 && s.days <= 90; });
      return true;
    })();
    return matchSearch && matchStatus && matchDept && matchCountry && matchLang && matchSkill && matchTravel && matchDoc;
  });

  // ── Open edit (async: loads files from disk if storage connected) ────────
  const openEdit = async fr => {
    const firstName = fr.firstName !== undefined ? fr.firstName : (fr.name || '').split(/\s+/)[0] || '';
    const lastName  = fr.firstName !== undefined ? fr.lastName  : (fr.name || '').split(/\s+/).slice(1).join(' ') || '';
    // migrate old documents[] → otherDocs[]
    const otherDocs = fr.otherDocs ?? (fr.documents || []).map((d, idx) => ({
      id: `legacy-${idx}`, title: d.type || '', expiry: d.expiry || '', files: [],
    }));
    // ensure visas have files array
    const visas = (fr.visas || []).map(v => ({ files: [], ...v }));

    let passportFiles = fr.passportFiles || [];
    let photoFiles    = fr.photoFiles    || [];
    let cvFiles       = fr.cvFiles       || [];

    // Load persisted files from disk (overrides empty in-memory arrays)
    if (storageDir && storageStatus === 'connected' && fr.id) {
      try {
        const disk = await loadFreelancerDocs(storageDir, fr.id);
        // Disk is authoritative; add any in-memory files with _raw not yet on disk
        const merge = (diskArr, memArr) => {
          const diskNames = new Set(diskArr.map(f => f.name));
          return [...diskArr, ...(memArr).filter(f => f._raw && !diskNames.has(f.name))];
        };
        passportFiles = merge(disk.passportFiles, passportFiles);
        photoFiles    = merge(disk.photoFiles,    photoFiles);
        cvFiles       = merge(disk.cvFiles,       cvFiles);
      } catch {}
    }

    setEditModal({
      ...fr,
      firstName,
      lastName,
      passportFiles,
      photoFiles,
      cvFiles,
      visas,
      otherDocs,
      customSkills:  { ...EMPTY_FREELANCER.customSkills, ...fr.customSkills },
      address:       { ...fr.address },
      finance:       { ...fr.finance },
      middleName:          fr.middleName ?? '',
      additionalPassports: fr.additionalPassports || [],
      departmentLevels:    fr.departmentLevels || {},
      languagesDetail:     fr.languagesDetail || [],
      emergencyContact:    { phone: '', email: '', ...fr.emergencyContact },
      travelPortDetail:    { country: '', city: '', code: '', ...fr.travelPortDetail },
      bankAccounts:        fr.bankAccounts || [],
      freelancerComment:   fr.freelancerComment ?? '',
    });
  };

  // ── Actions ───────────────────────────────────────────────────────────────
  const updateF = (id, patch) => {
    setFreelancers(fs => fs.map(f => f.id === id ? { ...f, ...patch } : f));
    if (selected?.id === id) setSelected(s => ({ ...s, ...patch }));
  };
  const approve = async id => {
    try { await api.freelancers.setStatus(id, 'active'); } catch {}
    updateF(id, { status: 'active' });
  };
  const block = async id => {
    try { await api.freelancers.setStatus(id, 'blocked'); } catch {}
    updateF(id, { status: 'blocked' });
  };
  const remind = async id => {
    try {
      await api.freelancers.remind(id);
      alert('Reminder email sent.');
    } catch (e) { alert('Failed to send reminder: ' + e.message); }
  };
  // Запросити (або повторно запросити) "довідникову" картку без userId на
  // портал — той самий ендпоінт і для першого запрошення, і для Resend.
  const invite = async id => {
    try {
      const res = await api.freelancers.invite(id);
      updateF(id, { invitedAt: res.invitedAt, inviteExpired: false });
      alert('Invitation email sent.');
    } catch (e) { alert('Failed to send invitation: ' + e.message); }
  };
  // Один лист з усіма позначеними🚩 документами цього фрілансера одразу
  // (а не окремий лист на кожну позначку) — свідома ручна дія адміна.
  const notifyFlagged = async id => {
    try {
      const res = await api.freelancers.notifyFlaggedDocuments(id);
      alert(`Notified about ${res.count} flagged document${res.count === 1 ? '' : 's'}.`);
    } catch (e) { alert('Failed to send notification: ' + e.message); }
  };
  // Масове нагадування — сервер відповідає одразу (не чекаючи, поки розішлються
  // усі листи: на десятках отримувачів це раніше впиралось у 504 від
  // reverse-proxy), тож тут бачимо лише "скільки поставлено в чергу", а не
  // фінальні sent/failed — фактична розсилка триває у фоні на бекенді.
  const remindAll = async () => {
    setRemindAllBusy(true);
    try {
      const res = await api.freelancers.remindAll();
      const lines = [`Queued: ${res.queued} reminder${res.queued === 1 ? '' : 's'} — sending now in the background.`];
      if (res.skippedCooldown) lines.push(`Skipped (reminded within the last 3 days): ${res.skippedCooldown}`);
      alert(lines.join('\n'));
    } catch (e) {
      alert('Failed to start sending reminders: ' + e.message);
    } finally {
      setRemindAllBusy(false);
      setConfirmRemindAll(false);
    }
  };
  const saveTask = async form => {
    try { await addTask(form); }
    catch (e) { alert('Failed to create task: ' + e.message); }
  };
  const remove = async id => {
    try { await api.freelancers.delete(id); } catch (e) { console.warn('API delete error:', e); }
    setFreelancers(fs => fs.filter(f => f.id !== id));
    setSelected(null);
    setConfirmDel(null);
  };
  // ── Admin document management (реальні файли на сервері) ─────────────────
  const [docUploading, setDocUploading] = useState({}); // category -> bool
  const uploadAdminDoc = async (freelancerId, category, fileList, extra = {}) => {
    setDocUploading(u => ({ ...u, [category]: true }));
    try {
      const res = await api.freelancers.uploadDocuments(freelancerId, category, fileList, extra);
      const key = category === 'passport' ? 'passportFiles' : category === 'photo' ? 'photoFiles' : category === 'cv' ? 'cvFiles' : null;
      const fr = freelancers.find(f => f.id === freelancerId) || selected;
      if (key) {
        updateF(freelancerId, { [key]: [...(fr?.[key] || []), ...res.documents] });
      } else {
        const newOtherDocs = res.documents.map(d => ({ id: d.id, title: extra.title || d.name, expiry: extra.expiry || '', files: [d] }));
        updateF(freelancerId, { otherDocs: [...(fr?.otherDocs || []), ...newOtherDocs] });
      }
      return res.documents; // потрібно EditForm-у, щоб відобразити реально збережені файли одразу (04.08.2026)
    } catch (e) { alert('Upload failed: ' + e.message); }
    finally { setDocUploading(u => ({ ...u, [category]: false })); }
  };
  const removeAdminDoc = async (freelancerId, category, docId) => {
    try {
      await api.freelancers.deleteDocument(freelancerId, docId);
      const fr = freelancers.find(f => f.id === freelancerId) || selected;
      if (category === 'other') {
        updateF(freelancerId, { otherDocs: (fr?.otherDocs || []).filter(d => d.id !== docId) });
      } else {
        const key = category === 'passport' ? 'passportFiles' : category === 'photo' ? 'photoFiles' : 'cvFiles';
        updateF(freelancerId, { [key]: (fr?.[key] || []).filter(f => f.id !== docId) });
      }
    } catch (e) { alert('Failed to remove file: ' + e.message); }
  };
  // Позначити (reason непорожній) або зняти позначку (reason='') з документа —
  // застосовується до будь-якої категорії, бо файл усередині otherDocs[].files
  // теж лежить у форматі docFile(), тому патчимо його однаково скрізь.
  const flagAdminDoc = async (freelancerId, category, docId, reason) => {
    const res = await api.freelancers.flagDocument(freelancerId, docId, reason); // кидає при помилці — модалка сама покаже
    const patch = { flaggedAt: res.document.flaggedAt, flagReason: res.document.flagReason };
    const fr = freelancers.find(f => f.id === freelancerId) || selected;
    if (category === 'other') {
      updateF(freelancerId, {
        otherDocs: (fr?.otherDocs || []).map(d => ({
          ...d,
          files: (d.files || []).map(f => f.id === docId ? { ...f, ...patch } : f),
        })),
      });
    } else {
      const key = category === 'passport' ? 'passportFiles' : category === 'photo' ? 'photoFiles' : category === 'cv' ? 'cvFiles' : 'consentDocs';
      updateF(freelancerId, { [key]: (fr?.[key] || []).map(f => f.id === docId ? { ...f, ...patch } : f) });
    }
    // Примітка: позначка/зняття перераховує onboarding_complete на бекенді,
    // але тут не перераховуємо його локально (логіка nontrivial) — бейдж
    // Onboarding у таблиці підхопить актуальний стан при наступному refetch.
  };

  const save = async nf => {
    const isNew = !nf.id || !freelancers.find(x => x.id === nf.id);

    // Strip _raw from file objects before sending to API/state
    const clean = files => (files || []).map(({ _raw, ...rest }) => rest);
    const payload = {
      ...nf,
      passportFiles: clean(nf.passportFiles),
      photoFiles:    clean(nf.photoFiles),
      cvFiles:       clean(nf.cvFiles),
      visas:     (nf.visas     || []).map(v => ({ ...v, files: clean(v.files) })),
      otherDocs: (nf.otherDocs || []).map(d => ({ ...d, files: clean(d.files) })),
    };

    let saved;
    try {
      if (isNew) {
        const res = await api.freelancers.create(payload);
        saved = res.freelancer;
      } else {
        const res = await api.freelancers.update(nf.id, payload);
        saved = res.freelancer;
      }
    } catch (e) {
      console.warn('API save error, falling back to local state:', e);
      // Fallback: keep working without API
      saved = { ...payload, id: nf.id || Date.now() };
    }

    // Write files to local disk (File System Access API)
    if (storageDir && storageStatus === 'connected') {
      try {
        await saveFreelancerDocs(storageDir, saved.id, { ...nf, id: saved.id });
        const prev = freelancers.find(x => x.id === saved.id);
        if (prev) {
          await deleteRemovedFiles(storageDir, saved.id, 'passport', prev.passportFiles, saved.passportFiles);
          await deleteRemovedFiles(storageDir, saved.id, 'photo',    prev.photoFiles,    saved.photoFiles);
          await deleteRemovedFiles(storageDir, saved.id, 'cv',       prev.cvFiles,       saved.cvFiles);
        }
      } catch (e) { console.warn('Storage write error:', e); }
    }

    if (isNew) {
      setFreelancers(fs => [...fs, saved]);
    } else {
      setFreelancers(fs => fs.map(x => x.id === saved.id ? saved : x));
      if (selected?.id === saved.id) setSelected(saved);
    }
    setEditModal(null);
  };

  // ── Checkboxes ────────────────────────────────────────────────────────────
  const toggleCheck = (id, e) => { e.stopPropagation(); setCheckedIds(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const checkAll    = () => setCheckedIds(checkedIds.size === filtered.length && filtered.length > 0 ? new Set() : new Set(filtered.map(f => f.id)));

  // ── Columns ───────────────────────────────────────────────────────────────
  const toggleCol  = key => setVisibleCols(s => {
    const n = new Set(s);
    n.has(key) ? n.delete(key) : n.add(key);
    try { localStorage.setItem(COLS_STORAGE_KEY, JSON.stringify([...n])); } catch {}
    return n;
  });
  const visColumns = ALL_COLUMNS.filter(c => visibleCols.has(c.key));

  // ── Cell renderer ─────────────────────────────────────────────────────────
  const renderCell = (f, key) => {
    switch (key) {
      case 'name': return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Avatar name={f.name} size={32} />
          <div>
            <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '14px' }}>{f.name}</div>
            <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>{f.role}</div>
          </div>
        </div>
      );
      case 'departments': return (
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {f.departments?.map(d => (
            <span key={d} style={{ background: DEPT_COLOR[d]+'22', color: DEPT_COLOR[d], border: `1px solid ${DEPT_COLOR[d]}44`, borderRadius: '4px', padding: '1px 6px', fontSize: '11px', fontWeight: 700 }}>{d}</span>
          ))}
        </div>
      );
      case 'skills': return (
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', maxWidth: '180px' }}>
          {(f.skills||[]).slice(0,3).map(s => (
            <span key={s} style={{ background: 'var(--border)', color: 'var(--text-muted)', border: '1px solid var(--border-strong)', borderRadius: '4px', padding: '1px 6px', fontSize: '11px' }}>{s.replace(/^[A-Z]+-/,'')}</span>
          ))}
          {(f.skills||[]).length > 3 && <span style={{ color: 'var(--text-dim)', fontSize: '11px' }}>+{f.skills.length-3}</span>}
        </div>
      );
      case 'status': return <StatusBadge status={f.status} />;
      case 'country': return <span style={{ color: 'var(--text-muted)' }}>{f.address?.country || '—'}</span>;
      case 'email': return <span style={{ color: 'var(--text-muted)' }}>{f.email || '—'}</span>;
      case 'phone': return <span style={{ color: 'var(--text-muted)' }}>{f.phone || '—'}</span>;
      case 'travelPort': return <span style={{ color: 'var(--text-muted)' }}>{f.travelPort || '—'}</span>;
      case 'languages': return <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{(f.languagesDetail||[]).map(l => `${l.language} (${l.level})`).join(', ') || '—'}</span>;
      case 'visas': return (
        <div>
          {(f.visas||[]).map((v,i) => {
            const s = docExpiryStatus(v.expiry);
            return <div key={i} style={{ fontSize: '11px', color: s?.color || 'var(--text-muted)' }}>{v.country} {formatDate(v.expiry)}</div>;
          })}
        </div>
      );
      case 'projects': return (
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', maxWidth: '200px' }}>
          {(f.projects||[]).length === 0 && <span style={{ color: 'var(--text-disabled)' }}>—</span>}
          {(f.projects||[]).slice(0,2).map(p => (
            <span key={p} style={{ background: 'var(--info-bg)', color: 'var(--info-text)', border: '1px solid #3b82f644', borderRadius: '4px', padding: '1px 6px', fontSize: '11px', whiteSpace: 'nowrap' }}>{p}</span>
          ))}
          {(f.projects||[]).length > 2 && <span style={{ color: 'var(--text-dim)', fontSize: '11px' }}>+{f.projects.length-2}</span>}
        </div>
      );
      case 'registered': return <span style={{ color: 'var(--text-dim)' }}>{formatDate(f.registered)}</span>;
      case 'dob': return <span style={{ color: 'var(--text-muted)' }}>{formatDate(f.dob) || '—'}</span>;
      case 'passportExpiry': {
        const s = docExpiryStatus(f.passportExpiry);
        return f.passportExpiry
          ? <span style={{ color: s?.color || 'var(--text-muted)' }}>{formatDate(f.passportExpiry)}<ExpiryBadge expiry={f.passportExpiry} /></span>
          : <span style={{ color: 'var(--text-disabled)' }}>—</span>;
      }
      case 'emergencyContact': return (
        <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
          {[f.emergencyContact?.phone, f.emergencyContact?.email].filter(Boolean).join(' · ') || '—'}
        </span>
      );
      case 'bankAccounts': return (
        <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
          {(f.bankAccounts||[]).length ? `${f.bankAccounts.length} account${f.bankAccounts.length>1?'s':''} (${f.bankAccounts.map(a=>a.currency).join(', ')})` : '—'}
        </span>
      );
      case 'address': return (
        <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
          {[f.address?.street, f.address?.city, f.address?.zip].filter(Boolean).join(', ') || '—'}
        </span>
      );
      case 'whatsapp': return <span style={{ color: 'var(--text-muted)' }}>{f.whatsapp || '—'}</span>;
      case 'additionalPhone': return <span style={{ color: 'var(--text-muted)' }}>{f.additionalPhone || '—'}</span>;
      case 'currency': return <span style={{ color: 'var(--text-muted)' }}>{f.finance?.currency || '—'}</span>;
      case 'passportNumber': return <span style={{ color: 'var(--text-muted)' }}>{f.passportNumber || '—'}</span>;
      case 'gender': return <span style={{ color: 'var(--text-muted)' }}>{f.gender || '—'}</span>;
      case 'nationality': return <span style={{ color: 'var(--text-muted)' }}>{f.nationality || '—'}</span>;
      case 'maritalStatus': return <span style={{ color: 'var(--text-muted)' }}>{f.maritalStatus || '—'}</span>;
      case 'birthPlace': return <span style={{ color: 'var(--text-muted)' }}>{[f.birthCity, f.birthCountry].filter(Boolean).join(', ') || '—'}</span>;
      case 'driveLink': return f.driveLink
        ? <a href={f.driveLink} target="_blank" rel="noreferrer" style={{ color: 'var(--info-text)' }} onClick={e => e.stopPropagation()}>📁 Open</a>
        : <span style={{ color: 'var(--text-disabled)' }}>—</span>;
      case 'onboarding': {
        // Картка без userId — це довідниковий запис, доданий адміном вручну,
        // не зареєстрований учасник порталу (04.08.2026). Onboarding-стадії
        // нижче стосуються лише тих, хто вже має акаунт.
        if (!f.userId) {
          return f.invitedAt
            ? <span style={{ color: 'var(--info-text)', fontSize: '11px', fontWeight: 700 }} title={`Invited ${new Date(f.invitedAt).toLocaleDateString()}${f.inviteExpired ? ' — link expired, resend' : ''}`}>
                {f.inviteExpired ? '✉️ Invite expired' : '✉️ Invited'}
              </span>
            : <span style={{ color: 'var(--text-muted)', fontSize: '11px', fontWeight: 700 }} title="Roster-only card — not a portal account">📇 Not on portal</span>;
        }
        const flagged = flaggedDocuments(f);
        const stage = !f.guideAckAt ? 'Guide not read'
          : !f.consentSignedAt      ? 'Consent not signed'
          : flagged.length > 0      ? `${flagged.length} document${flagged.length > 1 ? 's' : ''} flagged`
          : !f.onboardingComplete   ? 'Profile incomplete'
          : null;
        const flaggedStage = flagged.length > 0 && f.guideAckAt && f.consentSignedAt;
        return (
          <span style={{ color: stage ? (flaggedStage ? 'var(--accent)' : 'var(--warning-text)') : 'var(--success-text)', fontSize: '11px', fontWeight: 700 }} title={stage || 'Fully registered'}>
            {stage ? `${flaggedStage ? '🚩' : '⏳'} ${stage}` : '✓ Complete'}
          </span>
        );
      }
      case 'comment': return <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{f.comment || '—'}</span>;
      case 'freelancerComment': return <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{f.freelancerComment || '—'}</span>;
      default: return '—';
    }
  };

  // ── Select styles ─────────────────────────────────────────────────────────
  const selStyle = { background: 'var(--bg-input)', border: '1px solid var(--border-strong)', borderRadius: '6px', color: 'var(--text-body)', padding: '6px 10px', fontSize: '13px', outline: 'none', cursor: 'pointer' };
  const selActive = { ...selStyle, borderColor: RED, color: RED };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)', fontFamily: 'Arial, sans-serif' }}>

      {/* Nav */}
      <div style={{ background: 'var(--bg-card)', padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={onBack}
            style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-muted)', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
            ← Back
          </button>
          <span style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '18px', letterSpacing: '2px' }}>FREELANCERS</span>
          <span style={{ color: 'var(--text-dim)', fontSize: '13px' }}>{freelancers.length} total · {counts.active} active · {counts.pending} pending</span>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {checkedIds.size > 0 && (
            <button onClick={() => exportToCSV(freelancers.filter(f => checkedIds.has(f.id)))}
              style={{ background: 'var(--success-bg)', border: '1px solid var(--success-text)', color: 'var(--success-text)', borderRadius: '8px', padding: '7px 14px', fontWeight: 700, cursor: 'pointer', fontSize: '13px' }}>
              ↓ Export {checkedIds.size} to CSV
            </button>
          )}

          {/* View toggle */}
          <div style={{ display: 'flex', border: '1px solid var(--border-strong)', borderRadius: '6px', overflow: 'hidden' }}>
            {['table','grid'].map(v => (
              <button key={v} onClick={() => setView(v)}
                style={{ padding: '7px 14px', border: 'none', cursor: 'pointer', fontSize: '13px', background: view === v ? RED : 'transparent', color: view === v ? 'var(--text-primary)' : 'var(--text-dim)' }}>
                {v === 'table' ? '☰ Table' : '⊞ Grid'}
              </button>
            ))}
          </div>

          {/* Column picker */}
          {view === 'table' && (
            <div style={{ position: 'relative' }}>
              <button onClick={() => setShowColPicker(p => !p)}
                style={{ padding: '7px 14px', border: '1px solid var(--border-strong)', borderRadius: '6px', background: showColPicker ? 'var(--border)' : 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '13px' }}>
                Columns
              </button>
              {showColPicker && (
                <div style={{ position: 'absolute', right: 0, top: '38px', background: 'var(--bg-menu)', border: '1px solid var(--border-strong)', borderRadius: '10px', padding: '12px 16px', zIndex: 200, minWidth: '180px', boxShadow: '0 8px 24px rgba(0,0,0,0.6)' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>Show / Hide Columns</div>
                  {ALL_COLUMNS.map(col => (
                    <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--text-body)', padding: '4px 0', cursor: 'pointer' }}>
                      <input type="checkbox" checked={visibleCols.has(col.key)} onChange={() => toggleCol(col.key)} />
                      {col.label}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Storage indicator */}
          {storageStatus === 'connected' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', border: '1px solid #22c55e44', borderRadius: '6px', background: 'var(--success-bg)' }}>
              <span style={{ color: 'var(--success-text)', fontSize: '10px' }}>●</span>
              <span style={{ color: 'var(--success-text)', fontSize: '12px', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{storageName}</span>
              <button onClick={connectStorage} title="Change folder"
                style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: '12px', padding: '0 2px' }}>↺</button>
            </div>
          )}
          {storageStatus === 'disconnected' && (
            <button onClick={reconnectStorage}
              style={{ padding: '6px 12px', border: '1px solid var(--warning-text)', borderRadius: '6px', background: 'var(--warning-bg)', color: 'var(--warning-text)', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
              ↺ Reconnect: {storageName}
            </button>
          )}
          {storageStatus === 'none' && (
            <button onClick={connectStorage}
              style={{ padding: '6px 12px', border: '1px solid var(--border-strong)', borderRadius: '6px', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '12px' }}>
              📁 Connect storage
            </button>
          )}

          {isAdmin && onboardingIncompleteCount > 0 && (
            <button onClick={() => setConfirmRemindAll(true)}
              title="Send a reminder email to everyone with incomplete onboarding"
              style={{ background: 'transparent', color: 'var(--info-text)', border: '1px solid var(--info-text)', borderRadius: '8px', padding: '8px 18px', fontWeight: 700, cursor: 'pointer', fontSize: '13px' }}>
              🔔 Remind everyone ({onboardingIncompleteCount})
            </button>
          )}

          {isAdmin && (
            <button onClick={() => setEditModal({ ...EMPTY_FREELANCER })}
              style={{ background: RED, color: 'var(--text-primary)', border: 'none', borderRadius: '8px', padding: '8px 18px', fontWeight: 700, cursor: 'pointer', fontSize: '13px' }}>
              + Add Freelancer
            </button>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ padding: '12px 32px', background: 'var(--bg-card)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="🔍  Search name, email, phone, country, project…"
            style={{ background: 'var(--bg-input)', border: '1px solid var(--border-strong)', borderRadius: '6px', color: 'var(--text-body)', padding: '7px 12px', fontSize: '13px', width: '280px', outline: 'none' }}
          />

          {/* Status pills */}
          <div style={{ display: 'flex', gap: '6px' }}>
            {['All','active','pending','blocked'].map(s => {
              const cfg = STATUS_COLOR[s] || { color: 'var(--accent)', border: 'var(--accent)', bg: 'var(--danger-bg)' };
              const active = statusFilter === s;
              return (
                <button key={s} onClick={() => setStatusFilter(s)}
                  style={{
                    padding: '5px 12px', borderRadius: '99px', border: `1px solid ${active ? (s === 'All' ? RED : cfg.border) : 'var(--border-strong)'}`,
                    cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                    background: active ? (s === 'All' ? 'var(--danger-bg)' : cfg.bg) : 'transparent',
                    color: active ? (s === 'All' ? RED : cfg.color) : 'var(--text-dim)',
                  }}>
                  {s === 'All' ? `All (${freelancers.length})` : `${STATUS_LABEL[s]} (${counts[s]||0})`}
                </button>
              );
            })}
          </div>

          {/* Dept filter */}
          <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)}
            style={deptFilter !== 'All' ? { ...selActive } : { ...selStyle }}>
            <option value="All">All Depts</option>
            {DEPARTMENTS.map(d => <option key={d} value={d}>{d} – {DEPT_FULL[d]}</option>)}
          </select>

          {/* Filters toggle */}
          <button onClick={() => setShowFilters(p => !p)}
            style={{ marginLeft: 'auto', padding: '6px 14px', border: `1px solid ${activeFilterCount > 0 ? RED : 'var(--border-strong)'}`, borderRadius: '6px', background: showFilters ? 'var(--border)' : 'transparent', color: showFilters ? 'var(--text-body)' : (activeFilterCount > 0 ? RED : 'var(--text-muted)'), cursor: 'pointer', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
            ⚙ Filters {activeFilterCount > 0 && <span style={{ background: RED, color: 'var(--text-primary)', borderRadius: '10px', padding: '0 6px', fontSize: '11px', fontWeight: 700 }}>{activeFilterCount}</span>}
          </button>
          {activeFilterCount > 0 && (
            <button onClick={() => { setCountryFilter('All'); setLangFilter('All'); setSkillFilter('All'); setTravelFilter('All'); setDocFilter('All'); }}
              style={{ padding: '6px 12px', border: '1px solid var(--border-strong)', borderRadius: '6px', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '12px' }}>
              ✕ Clear
            </button>
          )}
        </div>

        {/* Advanced filters */}
        {showFilters && (
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginTop: '10px', paddingTop: '10px', borderTop: '1px solid var(--border)' }}>
            <select value={countryFilter} onChange={e => setCountryFilter(e.target.value)} style={countryFilter !== 'All' ? selActive : selStyle}>
              {allCountries.map(c => <option key={c} value={c}>{c === 'All' ? 'All Countries' : c}</option>)}
            </select>
            <select value={langFilter} onChange={e => setLangFilter(e.target.value)} style={langFilter !== 'All' ? selActive : selStyle}>
              {allLanguages.map(l => <option key={l} value={l}>{l === 'All' ? 'All Languages' : l}</option>)}
            </select>
            <select value={skillFilter} onChange={e => setSkillFilter(e.target.value)} style={{ ...(skillFilter !== 'All' ? selActive : selStyle), maxWidth: '160px' }}>
              {allSkills.map(s => <option key={s} value={s}>{s === 'All' ? 'All Skills' : s.replace(/^[A-Z]+-/, '')}</option>)}
            </select>
            <select value={travelFilter} onChange={e => setTravelFilter(e.target.value)} style={travelFilter !== 'All' ? selActive : selStyle}>
              <option value="All">Travel Port: Any</option>
              <option value="yes">Has Travel Port</option>
              <option value="no">No Travel Port</option>
            </select>
            <select value={docFilter} onChange={e => setDocFilter(e.target.value)} style={docFilter !== 'All' ? selActive : selStyle}>
              <option value="All">Docs: All</option>
              <option value="expiring">Expiring (≤90d)</option>
              <option value="expired">Expired</option>
            </select>
            <span style={{ color: 'var(--text-disabled)', fontSize: '12px', marginLeft: 'auto' }}>{filtered.length} of {freelancers.length}</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{ padding: '28px 32px', display: 'flex', gap: '20px', marginRight: selected ? '380px' : 0, transition: 'margin-right 0.2s' }}>

        {/* TABLE */}
        {view === 'table' && (
          <div style={{ flex: 1, background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr>
                  <th style={TH}><input type="checkbox" checked={checkedIds.size === filtered.length && filtered.length > 0} onChange={checkAll} /></th>
                  {visColumns.map(c => <th key={c.key} style={TH}>{c.label}</th>)}
                  <th style={{ ...TH, cursor: 'default' }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={visColumns.length+2} style={{ textAlign: 'center', padding: '48px', color: 'var(--text-disabled)' }}>No freelancers found</td></tr>
                )}
                {filtered.map(f => {
                  const isActive = selected?.id === f.id;
                  return (
                    <tr key={f.id}
                      onClick={() => setSelected(isActive ? null : f)}
                      style={{ cursor: 'pointer', background: isActive ? 'var(--bg-hover)' : 'transparent', borderLeft: isActive ? '3px solid var(--accent)' : '3px solid transparent' }}
                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}>
                      <td style={TD} onClick={e => toggleCheck(f.id, e)}>
                        <input type="checkbox" checked={checkedIds.has(f.id)} onChange={() => {}} />
                      </td>
                      {visColumns.map(c => <td key={c.key} style={TD}>{renderCell(f, c.key)}</td>)}
                      <td style={{ ...TD, whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                        <RowActionsMenu
                          freelancer={f}
                          isAdmin={isAdmin}
                          onApprove={() => approve(f.id)}
                          onBlock={() => block(f.id)}
                          onEdit={() => openEdit(f)}
                          onDelete={() => setConfirmDel(f)}
                          onRemind={() => remind(f.id)}
                          onTask={() => setTaskFor(f)}
                          onInvite={() => invite(f.id)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* GRID */}
        {view === 'grid' && (
          <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px,1fr))', gap: '16px', alignContent: 'start' }}>
            {filtered.map(f => {
              const isActive = selected?.id === f.id;
              return (
                <div key={f.id} onClick={() => setSelected(isActive ? null : f)}
                  style={{ background: 'var(--bg-card)', borderRadius: '12px', border: isActive ? `2px solid ${RED}` : '1px solid var(--border)', padding: '18px', cursor: 'pointer', position: 'relative' }}>
                  <input type="checkbox" checked={checkedIds.has(f.id)} onClick={e => toggleCheck(f.id, e)} onChange={() => {}} style={{ position: 'absolute', top: '12px', right: '12px' }} />
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '10px' }}>
                    <Avatar name={f.name} size={40} />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--text-primary)' }}>{f.name}</div>
                      <StatusBadge status={f.status} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '8px' }}>
                    {f.departments?.map(d => (
                      <span key={d} style={{ background: DEPT_COLOR[d]+'22', color: DEPT_COLOR[d], border: `1px solid ${DEPT_COLOR[d]}44`, borderRadius: '4px', padding: '1px 6px', fontSize: '11px', fontWeight: 700 }}>{d}</span>
                    ))}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-dim)' }}>{f.address?.country} · {f.travelPort||'—'}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-dim)' }}>{f.email}</div>
                  {f.visas?.some(v => docExpiryStatus(v.expiry)?.days < 90) && (
                    <div style={{ fontSize: '11px', color: 'var(--warning-text)', marginTop: '6px' }}>⚠ Visa expiring soon</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* SIDE PANEL */}
      {selected && (
        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: '380px',
          background: 'var(--bg-panel)', borderLeft: '1px solid var(--border)',
          zIndex: 500, display: 'flex', flexDirection: 'column',
          fontFamily: 'Arial, sans-serif', overflowY: 'auto',
        }}>
          {/* Header */}
          <div style={{ padding: '24px 24px 20px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg-panel)', zIndex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <Avatar name={selected.name} size={48} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: '16px', color: 'var(--text-primary)' }}>{selected.name}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-dim)', marginBottom: '6px' }}>{selected.role} · {selected.gender}</div>
                  <StatusBadge status={selected.status} />
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <a href={freelancerTabHref(selected.id)} target="_blank" rel="noreferrer" title="Open in new tab"
                  style={{ color: 'var(--info-text)', fontSize: '13px', textDecoration: 'none', border: '1px solid var(--border-strong)', borderRadius: '6px', padding: '6px 10px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  ↗
                </a>
                <button onClick={() => setSelected(null)}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', fontSize: '22px', lineHeight: 1 }}>×</button>
              </div>
            </div>
            {isAdmin && (
              <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                {selected.status === 'pending' && (
                  <button onClick={() => approve(selected.id)} style={{ flex: 1, background: 'var(--success-bg)', border: '1px solid var(--success-text)', color: 'var(--success-text)', padding: '8px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>✓ Approve</button>
                )}
                {selected.status === 'active' && (
                  <button onClick={() => block(selected.id)} style={{ flex: 1, background: 'var(--warning-bg)', border: '1px solid var(--warning-text)', color: 'var(--warning-text)', padding: '8px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>⊘ Block</button>
                )}
                {selected.status === 'blocked' && (
                  <button onClick={() => approve(selected.id)} style={{ flex: 1, background: 'var(--info-bg)', border: '1px solid var(--info-text)', color: 'var(--info-text)', padding: '8px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>↺ Unblock</button>
                )}
                {selected.status === 'active' && isRegistrationIncomplete(selected) && (
                  <button onClick={() => remind(selected.id)} title="Send a reminder to finish registration"
                    style={{ flex: 1, background: 'var(--info-bg)', border: '1px solid var(--info-text)', color: 'var(--info-text)', padding: '8px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>🔔 Remind</button>
                )}
                {flaggedDocuments(selected).length > 0 && (
                  <button onClick={() => notifyFlagged(selected.id)} title="Email the freelancer the list of flagged documents"
                    style={{ flex: 1, background: 'var(--danger-bg)', border: '1px solid var(--accent)', color: RED, padding: '8px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>🚩 Notify ({flaggedDocuments(selected).length})</button>
                )}
                {!selected.userId && (
                  <button onClick={() => invite(selected.id)} title={selected.invitedAt ? 'Resend the portal invitation email' : 'Invite this freelancer to register and complete onboarding'}
                    style={{ flex: 1, background: 'var(--info-bg)', border: '1px solid var(--info-text)', color: 'var(--info-text)', padding: '8px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                    ✉️ {selected.invitedAt ? 'Resend invite' : 'Invite to portal'}
                  </button>
                )}
                {selected.userId && (
                  <button onClick={() => setTaskFor(selected)} title="Assign a task to this freelancer"
                    style={{ flex: 1, background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-body)', padding: '8px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>📌 Task</button>
                )}
                <button
                  onClick={() => openEdit(selected)}
                  style={{ flex: 1, background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-body)', padding: '8px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
                  ✏️ Edit
                </button>
                <button onClick={() => setConfirmDel(selected)}
                  style={{ background: 'transparent', border: '1px solid #7f1d1d', color: RED, padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
                  🗑
                </button>
              </div>
            )}
          </div>

          <div style={{ padding: '20px 24px', flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>

            <PSection title="Professional">
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '8px' }}>
                {selected.departments?.map(d => (
                  <span key={d} style={{ background: DEPT_COLOR[d]+'22', color: DEPT_COLOR[d], border: `1px solid ${DEPT_COLOR[d]}44`, borderRadius: '4px', padding: '2px 8px', fontSize: '12px', fontWeight: 700 }}>{d} · {DEPT_FULL[d]}</span>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                {selected.skills?.map(s => (
                  <span key={s} style={{ background: 'var(--border)', color: 'var(--text-muted)', border: '1px solid var(--border-strong)', borderRadius: '4px', padding: '2px 7px', fontSize: '11px' }}>{s}</span>
                ))}
                {Object.entries(selected.customSkills||{}).flatMap(([dept,skills]) => skills.map(s => (
                  <span key={`${dept}-${s}`} style={{ background: 'var(--border)', color: 'var(--text-muted)', border: '1px solid var(--border-strong)', borderRadius: '4px', padding: '2px 7px', fontSize: '11px' }}>{dept}: {s}</span>
                )))}
              </div>
            </PSection>

            {selected.languages?.length > 0 && (
              <PSection title="Languages">
                {selected.languages.map((l,i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                    <span style={{ color: 'var(--text-body)' }}>{l.lang}</span>
                    <span style={{ color: 'var(--text-dim)' }}>{l.level}</span>
                  </div>
                ))}
              </PSection>
            )}

            <PSection title="Contact">
              <PRow label="Email"    value={selected.email} />
              <PRow label="Phone"    value={selected.phone} />
              <PRow label="WhatsApp" value={selected.whatsapp} />
              {selected.additionalPhone && <PRow label="Alt Phone" value={selected.additionalPhone} />}
              <PRow label="Travel Port" value={selected.travelPort} />
              {selected.address?.country && (
                <PRow label="Address" value={`${selected.address.street}, ${selected.address.city}, ${selected.address.country} ${selected.address.zip}`} />
              )}
            </PSection>

            <PSection title="Personal">
              <PRow label="Date of Birth"  value={formatDate(selected.dob)} />
              <PRow label="Nationality"    value={selected.nationality} />
              <PRow label="Passport №"     value={selected.passportNumber} />
              {selected.passportExpiry && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                  <span style={{ color: 'var(--text-dim)' }}>Passport expiry</span>
                  <span style={{ color: 'var(--text-body)' }}>{formatDate(selected.passportExpiry)}<ExpiryBadge expiry={selected.passportExpiry} /></span>
                </div>
              )}
              <PRow label="Marital Status" value={selected.maritalStatus} />
              <PRow label="Place of Birth" value={[selected.birthCity, selected.birthCountry].filter(Boolean).join(', ')} />
            </PSection>

            {selected.visas?.length > 0 && (
              <PSection title="Visas">
                {selected.visas.map((v,i) => {
                  const s = docExpiryStatus(v.expiry);
                  return (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px', alignItems: 'center' }}>
                      <span style={{ color: 'var(--text-body)' }}>🛂 {v.country}</span>
                      <span style={{ color: s?.color || 'var(--text-muted)' }}>{formatDate(v.expiry)}<ExpiryBadge expiry={v.expiry} /></span>
                    </div>
                  );
                })}
              </PSection>
            )}

            {/* Документи (реальні файли на сервері) — завантажені фрілансером через self-service,
                або адміном тут. Адмін може довантажити/видалити. */}
            <PSection title="Documents">
              {['passport', 'photo', 'cv'].map(cat => {
                const key = cat === 'passport' ? 'passportFiles' : cat === 'photo' ? 'photoFiles' : 'cvFiles';
                const label = cat === 'passport' ? 'Passport' : cat === 'photo' ? 'Photo' : 'CV';
                const files = selected[key] || [];
                return (
                  <div key={cat} style={{ marginBottom: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <span style={{ color: 'var(--text-dim)', fontSize: '11px' }}>{label}</span>
                      <label style={{ color: 'var(--info-text)', fontSize: '11px', cursor: docUploading[cat] ? 'not-allowed' : 'pointer', opacity: docUploading[cat] ? 0.6 : 1 }}>
                        {docUploading[cat] ? 'Uploading…' : '+ Upload'}
                        <input type="file" style={{ display: 'none' }} disabled={docUploading[cat]}
                          onChange={e => { if (e.target.files?.length) uploadAdminDoc(selected.id, cat, e.target.files); e.target.value = ''; }} />
                      </label>
                    </div>
                    {files.length === 0
                      ? <div style={{ color: 'var(--text-disabled)', fontSize: '11px' }}>Not uploaded</div>
                      : files.map(f => <ServerFileLink key={f.id} freelancerId={selected.id} file={f} onRemove={() => removeAdminDoc(selected.id, cat, f.id)} onFlag={reason => flagAdminDoc(selected.id, cat, f.id, reason)} />)}
                  </div>
                );
              })}
            </PSection>

            <PSection title="Other Documents">
              {(selected.otherDocs||[]).map((d,i) => (
                <div key={d.id||i} style={{ marginBottom: '8px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', padding: '10px 12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                    <span style={{ color: 'var(--text-body)', fontWeight: 600 }}>📄 {d.title}</span>
                    {d.expiry && <ExpiryBadge expiry={d.expiry} />}
                  </div>
                  {(d.files||[]).length > 0 && (
                    <div style={{ marginTop: '6px' }}>
                      {d.files.map(f => <ServerFileLink key={f.id} freelancerId={selected.id} file={f} onRemove={() => removeAdminDoc(selected.id, 'other', f.id)} onFlag={reason => flagAdminDoc(selected.id, 'other', f.id, reason)} />)}
                    </div>
                  )}
                </div>
              ))}
              <label style={{ color: 'var(--info-text)', fontSize: '12px', cursor: docUploading.other ? 'not-allowed' : 'pointer', opacity: docUploading.other ? 0.6 : 1 }}>
                {docUploading.other ? 'Uploading…' : '+ Add document'}
                <input type="file" style={{ display: 'none' }} disabled={docUploading.other}
                  onChange={e => { if (e.target.files?.length) uploadAdminDoc(selected.id, 'other', e.target.files); e.target.value = ''; }} />
              </label>
            </PSection>

            {(selected.consentDocs||[]).length > 0 && (
              <PSection title="Data Processing Consent">
                <div style={{ marginBottom: '8px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', padding: '10px 12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                    <span style={{ color: 'var(--text-body)', fontWeight: 600 }}>✍️ Signed electronically</span>
                    {selected.consentSignedAt && (
                      <span style={{ color: 'var(--success-text)', fontSize: '11px' }}>{new Date(selected.consentSignedAt).toLocaleString()}</span>
                    )}
                  </div>
                  {selected.consentDocs.map(f => <ServerFileLink key={f.id} freelancerId={selected.id} file={f} />)}
                </div>
              </PSection>
            )}

            {selected.driveLink && (
              <PSection title="Cloud Storage">
                <a href={selected.driveLink} target="_blank" rel="noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#1a73e8', color: 'var(--text-primary)', borderRadius: '8px', padding: '8px 14px', textDecoration: 'none', fontSize: '13px', fontWeight: 600 }}>
                  📁 Open Google Drive
                </a>
              </PSection>
            )}

            {canFinance && selected.finance?.bankName && (
              <PSection title="Financial">
                <PRow label="Bank"     value={selected.finance.bankName} />
                <PRow label="IBAN"     value={selected.finance.accountNumber} />
                <PRow label="SWIFT"    value={selected.finance.swift} />
                <PRow label="Currency" value={selected.finance.currency} />
              </PSection>
            )}

            {(selected.projects||[]).length > 0 && (
              <PSection title="Projects">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {selected.projects.map((p,i) => (
                    <div key={i} style={{ fontSize: '13px', color: 'var(--info-text)', background: 'var(--info-bg)', border: '1px solid #3b82f644', borderRadius: '6px', padding: '6px 10px' }}>📋 {p}</div>
                  ))}
                </div>
              </PSection>
            )}

            {selected.comment && (
              <PSection title="Comments">
                <div style={{ color: 'var(--text-muted)', fontSize: '13px', lineHeight: 1.6, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px' }}>
                  {selected.comment}
                </div>
              </PSection>
            )}
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModal && (
        <EditModal onClose={() => setEditModal(null)}>
          <EditForm initial={editModal} onSave={save} onCancel={() => setEditModal(null)} canFinance={canFinance}
            onUploadDoc={uploadAdminDoc} onRemoveDoc={removeAdminDoc} docUploading={docUploading} />
        </EditModal>
      )}

      {/* Delete Modal */}
      {confirmDel && (
        <DeleteModal
          freelancer={confirmDel}
          onConfirm={() => remove(confirmDel.id)}
          onCancel={() => setConfirmDel(null)}
        />
      )}

      {/* Remind Everyone Modal */}
      {confirmRemindAll && (
        <RemindAllModal
          count={onboardingIncompleteCount}
          busy={remindAllBusy}
          onConfirm={remindAll}
          onCancel={() => setConfirmRemindAll(false)}
        />
      )}

      {/* Task Panel — призначення задачі конкретному фрілансеру */}
      {taskFor && (
        <TaskPanel
          onClose={() => setTaskFor(null)}
          onSave={saveTask}
          projects={projects}
          allUsers={allUsers}
          initialAssignedTo={taskFor.userId}
        />
      )}
    </div>
  );
}

// ─── Edit Modal (dark, portal) ────────────────────────────────────────────────
export function EditModal({ children, onClose }) {
  return ReactDOM.createPortal(
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '20px' }}
      onClick={onClose}>
      <div
        style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px', padding: '28px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.7)', width: '660px', maxWidth: '95vw' }}
        onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>,
    document.body
  );
}

// ─── Edit Form ────────────────────────────────────────────────────────────────
const EDIT_TABS = ['Personal', 'Contact', 'Professional', 'Documents', 'Financial', 'Comments'];

export function EditForm({ initial, onSave, onCancel, canFinance, onUploadDoc, onRemoveDoc, docUploading = {} }) {
  const [f,   setF]   = useState({ ...initial });
  const [tab, setTab] = useState('Personal');
  const [customInputs, setCustomInputs] = useState(
    Object.fromEntries([...DEPARTMENTS, 'Other'].map(d => [d, '']))
  );

  const set     = (k, v) => setF(p => ({ ...p, [k]: v }));

  // ── Документи (passport/photo/cv/other) ─────────────────────────────────
  // На відміну від решти полів форми, документи зберігаються НЕ через
  // onSave() — а одразу реальним завантаженням на сервер (як і кнопка
  // "+ Upload" на картці фрілансера), бо PUT /:id їх повністю ігнорує, а
  // POST / писав лише title/expiry без самого файлу. Раніше UploadArea лише
  // тримала файл у пам'яті форми (blob URL) — після Save і перевідкриття
  // картки він щезав, хоча в БД нічого не з'являлося. Тому тут файл летить
  // на бекенд одразу при виборі, а не при натисканні Save (04.08.2026).
  const freelancerId = initial.id || null;
  const uploadReal = async (category, wrappedFiles, extra = {}) => {
    if (!freelancerId || !onUploadDoc) return null;
    const raw = wrappedFiles.map(x => x._raw).filter(Boolean);
    if (!raw.length) return null;
    return onUploadDoc(freelancerId, category, raw, extra);
  };
  // Passport/Photo/CV — одна категорія = один масив реальних docFile() записів.
  const uploadCatFile = async (category, key, wrappedFiles) => {
    if (!freelancerId) { alert('Save the profile first, then upload files.'); return; }
    const docs = await uploadReal(category, wrappedFiles);
    if (docs) set(key, [...(f[key]||[]), ...docs]);
  };
  const removeCatFile = async (category, key, docId) => {
    if (freelancerId && onRemoveDoc) {
      try { await onRemoveDoc(freelancerId, category, docId); }
      catch (e) { alert('Failed to remove file: ' + e.message); return; }
    }
    set(key, (f[key]||[]).filter(x => x.id !== docId));
  };
  const setAddr = (k, v) => setF(p => ({ ...p, address: { ...p.address, [k]: v } }));
  const setFin  = (k, v) => setF(p => ({ ...p, finance: { ...p.finance, [k]: v } }));
  const setCS   = (dept, arr) => setF(p => ({ ...p, customSkills: { ...p.customSkills, [dept]: arr } }));
  const setEmergency  = (k, v) => setF(p => ({ ...p, emergencyContact: { ...p.emergencyContact, [k]: v } }));
  const setTravelPort = (k, v) => setF(p => ({ ...p, travelPortDetail: { ...p.travelPortDetail, [k]: v } }));

  // Рівні по департаменту — мультивибір (той самий формат, що й онбординг фрілансера)
  const toggleDeptLevel = (dept, level) => setF(p => {
    const cur = p.departmentLevels?.[dept] || [];
    const next = cur.includes(level) ? cur.filter(l => l !== level) : [...cur, level];
    return { ...p, departmentLevels: { ...p.departmentLevels, [dept]: next } };
  });

  // Додаткові паспорти
  const addPassport    = ()           => setF(p => ({ ...p, additionalPassports: [...(p.additionalPassports||[]), { id: `new-${Date.now()}`, number: '', expiry: '' }] }));
  const setPassportF   = (i, k, v)    => setF(p => ({ ...p, additionalPassports: p.additionalPassports.map((x, idx) => idx === i ? { ...x, [k]: v } : x) }));
  const removePassport = i            => setF(p => ({ ...p, additionalPassports: p.additionalPassports.filter((_, idx) => idx !== i) }));

  // Банківські рахунки (декілька, кожен зі своєю валютою)
  const addBankAccount    = ()        => setF(p => ({ ...p, bankAccounts: [...(p.bankAccounts?.length ? p.bankAccounts : []), { bankName: '', accountNumber: '', swift: '', accountHolderName: '', currency: 'EUR' }] }));
  const setBankAccountF   = (i, k, v) => setF(p => ({ ...p, bankAccounts: p.bankAccounts.map((x, idx) => idx === i ? { ...x, [k]: v } : x) }));
  const removeBankAccount = i         => setF(p => ({ ...p, bankAccounts: p.bankAccounts.filter((_, idx) => idx !== i) }));

  // Additional skills (вільний список, без прив'язки до департаменту — той самий масив `skills`)
  const [newSkillInput, setNewSkillInput] = useState('');
  const addFreeSkill = () => {
    const v = newSkillInput.trim();
    if (!v || f.skills.includes(v)) return;
    set('skills', [...f.skills, v]);
    setNewSkillInput('');
  };
  const removeFreeSkill = s => set('skills', f.skills.filter(x => x !== s));
  // Префіксовані ("LX-Tech") — керуються чекбоксами вище; вільні — все інше.
  const freeSkills = (f.skills||[]).filter(s => !DEPARTMENTS.some(d => s.startsWith(d + '-')) && !s.startsWith('Other-'));

  const toggleDept = d => {
    if (f.departments.includes(d)) {
      set('departments', f.departments.filter(x => x !== d));
      set('skills', f.skills.filter(s => !s.startsWith(d + '-')));
    } else {
      set('departments', [...f.departments, d]);
    }
  };
  const toggleSkill = s => set('skills', f.skills.includes(s) ? f.skills.filter(x => x !== s) : [...f.skills, s]);

  const addCustomSkill = dept => {
    const val = (customInputs[dept] || '').trim();
    if (!val) return;
    const cur = f.customSkills?.[dept] || [];
    if (cur.length >= 10 || cur.includes(val)) return;
    setCS(dept, [...cur, val]);
    setCustomInputs(p => ({ ...p, [dept]: '' }));
  };
  const removeCustomSkill = (dept, skill) => setCS(dept, (f.customSkills?.[dept]||[]).filter(s => s !== skill));

  // Мови — зберігаються у languagesDetail (JSONB, {language, level}); той самий формат,
  // що й у self-service онбордингу фрілансера. Legacy `languages` (TEXT[]) більше не редагується тут.
  const addLang    = ()        => set('languagesDetail', [...(f.languagesDetail||[]), { language: '', level: 'Fluent' }]);
  const setLang    = (i, k, v) => set('languagesDetail', f.languagesDetail.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const removeLang = i         => set('languagesDetail', f.languagesDetail.filter((_, idx) => idx !== i));

  const [projInput, setProjInput] = useState('');
  const addProject    = () => { const v = projInput.trim(); if (!v || (f.projects||[]).includes(v)) return; set('projects', [...(f.projects||[]), v]); setProjInput(''); };
  const removeProject = p => set('projects', (f.projects||[]).filter(x => x !== p));

  // Visa helpers
  const addVisa          = ()           => set('visas', [...(f.visas||[]), { id: Date.now(), country: '', expiry: '', files: [] }]);
  const setVisa          = (i, k, v)   => set('visas', f.visas.map((v2, idx) => idx === i ? { ...v2, [k]: v } : v2));
  const removeVisa       = i           => set('visas', f.visas.filter((_, idx) => idx !== i));
  const addVisaFiles     = (i, files)  => set('visas', f.visas.map((v2, idx) => idx === i ? { ...v2, files: [...(v2.files||[]), ...files] } : v2));
  const removeVisaFile   = (i, fileId) => set('visas', f.visas.map((v2, idx) => idx === i ? { ...v2, files: (v2.files||[]).filter(x => x.id !== fileId) } : v2));

  // Other docs helpers — реальний аплоад/видалення (категорія 'other'). На
  // бекенді 1 запис freelancer_documents = 1 файл, тож видалення всього
  // "entry" видаляє і всі реально прикріплені до нього файли.
  const addOtherDoc = () => set('otherDocs', [...(f.otherDocs||[]), { id: Date.now(), title: '', expiry: '', files: [] }]);
  const setOtherDoc = (i, k, v) => set('otherDocs', (f.otherDocs||[]).map((d, idx) => idx === i ? { ...d, [k]: v } : d));
  const removeOtherDoc = async i => {
    const d = f.otherDocs[i];
    if (freelancerId && onRemoveDoc) {
      for (const file of (d.files||[])) {
        if (typeof file.id !== 'number') continue; // ще не завантажений реально — нема що видаляти на сервері
        try { await onRemoveDoc(freelancerId, 'other', file.id); }
        catch (e) { alert('Failed to remove file: ' + e.message); return; }
      }
    }
    set('otherDocs', (f.otherDocs||[]).filter((_, idx) => idx !== i));
  };
  const addOtherDocFiles = async (i, wrappedFiles) => {
    const d = f.otherDocs[i];
    if (!freelancerId) { alert('Save the profile first, then upload files.'); return; }
    const docs = await uploadReal('other', wrappedFiles, { title: d.title, expiry: d.expiry });
    if (!docs) return;
    set('otherDocs', (f.otherDocs||[]).map((doc, idx) => idx === i ? { ...doc, files: [...(doc.files||[]), ...docs] } : doc));
  };
  const removeOtherDocFile = async (i, fileId) => {
    if (freelancerId && onRemoveDoc && typeof fileId === 'number') {
      try { await onRemoveDoc(freelancerId, 'other', fileId); }
      catch (e) { alert('Failed to remove file: ' + e.message); return; }
    }
    set('otherDocs', (f.otherDocs||[]).map((d, idx) => idx === i ? { ...d, files: (d.files||[]).filter(x => x.id !== fileId) } : d));
  };

  const availSkillsForDept = d => (DEPT_SKILLS[d] || []).map(s => `${d}-${s}`);

  const I = { background: 'var(--bg-input)', border: '1px solid var(--border-strong)', borderRadius: '8px', color: 'var(--text-body)', padding: '8px 10px', fontSize: '13px', width: '100%', boxSizing: 'border-box', outline: 'none' };
  const L = { display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' };
  const G = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' };
  const S = { ...I, cursor: 'pointer' };

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)' }}>{initial.name ? 'Edit Freelancer' : 'Add Freelancer'}</h2>
        <button onClick={onCancel} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', fontSize: '22px', lineHeight: 1 }}>×</button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '2px', marginBottom: '20px', borderBottom: '1px solid var(--border)', overflowX: 'auto' }}>
        {EDIT_TABS.filter(t => t !== 'Financial' || canFinance).map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{ padding: '8px 12px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 700, borderBottom: tab === t ? `2px solid ${RED}` : '2px solid transparent', color: tab === t ? RED : 'var(--text-dim)', whiteSpace: 'nowrap' }}>
            {t}
          </button>
        ))}
      </div>

      <form onSubmit={e => { e.preventDefault(); onSave({ ...f, name: [f.lastName, f.firstName, f.middleName].filter(Boolean).join(' ') }); }} style={{ display: 'flex', flexDirection: 'column', gap: '14px', maxHeight: '56vh', overflowY: 'auto', paddingRight: '4px' }}>

        {tab === 'Personal' && <>
          <div style={G}>
            <div><label style={L}>Surname *</label><input style={I} value={f.lastName||''} onChange={e => set('lastName', e.target.value)} required /></div>
            <div><label style={L}>First Name *</label><input style={I} value={f.firstName||''} onChange={e => set('firstName', e.target.value)} required /></div>
          </div>
          <div><label style={L}>Middle Name</label><input style={I} value={f.middleName||''} onChange={e => set('middleName', e.target.value)} /></div>
          <div style={G}>
            <div><label style={L}>Gender</label>
              <select style={S} value={f.gender} onChange={e => set('gender', e.target.value)}><option value="">—</option>{GENDERS.map(g => <option key={g}>{g}</option>)}</select>
            </div>
            <div><label style={L}>Date of Birth</label><input type="date" style={I} value={f.dob} onChange={e => set('dob', e.target.value)} /></div>
          </div>
          <div style={G}>
            <div><label style={L}>Nationality</label><input style={I} value={f.nationality} onChange={e => set('nationality', e.target.value)} /></div>
            <div><label style={L}>Passport Number</label><input style={I} value={f.passportNumber} onChange={e => set('passportNumber', e.target.value)} /></div>
          </div>
          <div style={G}>
            <div><label style={L}>Passport Expiry</label><input type="date" style={I} value={f.passportExpiry} onChange={e => set('passportExpiry', e.target.value)} /></div>
            <div><label style={L}>Marital Status</label>
              <select style={S} value={f.maritalStatus} onChange={e => set('maritalStatus', e.target.value)}><option value="">—</option>{MARITAL.map(m => <option key={m}>{m}</option>)}</select>
            </div>
          </div>
          <div style={G}>
            <div><label style={L}>Birth Country</label><input style={I} value={f.birthCountry} onChange={e => set('birthCountry', e.target.value)} /></div>
            <div><label style={L}>Birth City</label><input style={I} value={f.birthCity} onChange={e => set('birthCity', e.target.value)} /></div>
          </div>

          {/* Additional passports */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={L}>Additional Passports</label>
              <button type="button" onClick={addPassport} style={{ background: 'none', border: 'none', color: RED, cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>+ Add</button>
            </div>
            {(f.additionalPassports||[]).map((p,i) => (
              <div key={p.id||i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                <input style={I} placeholder="Passport number" value={p.number} onChange={e => setPassportF(i,'number',e.target.value)} />
                <input type="date" style={I} value={p.expiry} onChange={e => setPassportF(i,'expiry',e.target.value)} />
                <button type="button" onClick={() => removePassport(i)} style={{ background: 'none', border: 'none', color: RED, cursor: 'pointer', fontSize: '16px' }}>✕</button>
              </div>
            ))}
          </div>

          <div style={G}>
            <div><label style={L}>Portal Role</label>
              <select style={S} value={f.role} onChange={e => set('role', e.target.value)}>{PORTAL_ROLES.map(r => <option key={r}>{r}</option>)}</select>
            </div>
            <div><label style={L}>Status</label>
              <select style={S} value={f.status} onChange={e => set('status', e.target.value)}>{['active','pending','blocked'].map(s => <option key={s}>{s}</option>)}</select>
            </div>
          </div>
        </>}

        {tab === 'Contact' && <>
          <div style={G}>
            <div><label style={L}>Email</label><input type="email" style={I} value={f.email} onChange={e => set('email', e.target.value)} /></div>
            <div><label style={L}>Phone</label><input style={I} value={f.phone} onChange={e => set('phone', e.target.value)} /></div>
          </div>
          <div style={G}>
            <div><label style={L}>WhatsApp</label><input style={I} value={f.whatsapp} onChange={e => set('whatsapp', e.target.value)} /></div>
            <div><label style={L}>Additional Phone</label><input style={I} value={f.additionalPhone} onChange={e => set('additionalPhone', e.target.value)} /></div>
          </div>
          <div><label style={L}>Travel Port</label><input style={I} value={f.travelPort} onChange={e => set('travelPort', e.target.value)} placeholder="e.g. Kyiv (KBP)" /></div>
          <hr style={{ border: 'none', borderTop: '1px solid var(--border)' }} />
          <p style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '1px' }}>Address</p>
          <div style={G}>
            <div><label style={L}>Country</label><input style={I} value={f.address?.country||''} onChange={e => setAddr('country', e.target.value)} /></div>
            <div><label style={L}>City</label><input style={I} value={f.address?.city||''} onChange={e => setAddr('city', e.target.value)} /></div>
          </div>
          <div style={G}>
            <div><label style={L}>Street Address</label><input style={I} value={f.address?.street||''} onChange={e => setAddr('street', e.target.value)} /></div>
            <div><label style={L}>ZIP / Postal Code</label><input style={I} value={f.address?.zip||''} onChange={e => setAddr('zip', e.target.value)} /></div>
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid var(--border)' }} />
          <p style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '1px' }}>Emergency Contact</p>
          <div style={G}>
            <div><label style={L}>Phone</label><input style={I} value={f.emergencyContact?.phone||''} onChange={e => setEmergency('phone', e.target.value)} /></div>
            <div><label style={L}>Email</label><input type="email" style={I} value={f.emergencyContact?.email||''} onChange={e => setEmergency('email', e.target.value)} /></div>
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid var(--border)' }} />
          <p style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '1px' }}>Port of Travel</p>
          <div style={G}>
            <div><label style={L}>Country</label><input style={I} value={f.travelPortDetail?.country||''} onChange={e => setTravelPort('country', e.target.value)} /></div>
            <div><label style={L}>City</label><input style={I} value={f.travelPortDetail?.city||''} onChange={e => setTravelPort('city', e.target.value)} /></div>
          </div>
          <div><label style={L}>Airport Code</label><input style={I} maxLength={10} value={f.travelPortDetail?.code||''} onChange={e => setTravelPort('code', e.target.value.toUpperCase().slice(0, 10))} placeholder="e.g. KBP" /></div>
        </>}

        {tab === 'Professional' && <>
          {/* Departments */}
          <div>
            <label style={L}>Departments</label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
              {DEPARTMENTS.map(d => (
                <button type="button" key={d} onClick={() => toggleDept(d)}
                  style={{ padding: '6px 12px', borderRadius: '6px', border: `2px solid ${f.departments.includes(d) ? DEPT_COLOR[d] : 'var(--border-strong)'}`, background: f.departments.includes(d) ? DEPT_COLOR[d]+'22' : 'transparent', color: f.departments.includes(d) ? DEPT_COLOR[d] : 'var(--text-dim)', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>
                  {d} <span style={{ fontWeight: 400, fontSize: '11px' }}>{DEPT_FULL[d]}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Skills per dept */}
          {f.departments.map(d => (
            <div key={d} style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: '10px', padding: '12px' }}>
              <p style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: 700, color: DEPT_COLOR[d] }}>{d} · {DEPT_FULL[d]}</p>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
                {availSkillsForDept(d).map(s => (
                  <button type="button" key={s} onClick={() => toggleSkill(s)}
                    style={{ padding: '3px 10px', borderRadius: '6px', border: `1.5px solid ${f.skills.includes(s) ? 'var(--text-body)' : 'var(--border-strong)'}`, background: f.skills.includes(s) ? 'var(--border)' : 'transparent', color: f.skills.includes(s) ? 'var(--text-primary)' : 'var(--text-dim)', fontSize: '12px', cursor: 'pointer' }}>
                    {s.replace(/^[A-Z]+-/,'')}
                  </button>
                ))}
              </div>
              {/* Custom skills */}
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '6px' }}>
                {(f.customSkills?.[d]||[]).map(s => (
                  <span key={s} style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: '6px', padding: '2px 8px', fontSize: '12px', color: 'var(--text-body)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {s}<button type="button" onClick={() => removeCustomSkill(d, s)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: RED, fontSize: '12px', padding: 0 }}>✕</button>
                  </span>
                ))}
              </div>
              {(f.customSkills?.[d]||[]).length < 10 && (
                <div style={{ display: 'flex', gap: '6px' }}>
                  <input style={{ ...I, flex: 1 }} placeholder="Add custom skill…" value={customInputs[d]||''} onChange={e => setCustomInputs(p => ({ ...p, [d]: e.target.value }))} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustomSkill(d))} />
                  <button type="button" onClick={() => addCustomSkill(d)} style={{ padding: '6px 12px', background: 'var(--border)', color: 'var(--text-body)', border: '1px solid var(--border-strong)', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>+</button>
                </div>
              )}
            </div>
          ))}

          {/* Other skills */}
          <div style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: '10px', padding: '12px' }}>
            <p style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: 700, color: 'var(--text-dim)' }}>Other Skills (Vectorworks, Autocad, etc.)</p>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
              {OTHER_BASE_SKILLS.map(s => {
                const key = `Other-${s}`;
                return (
                  <button type="button" key={key} onClick={() => toggleSkill(key)}
                    style={{ padding: '3px 10px', borderRadius: '6px', border: `1.5px solid ${f.skills.includes(key) ? 'var(--text-body)' : 'var(--border-strong)'}`, background: f.skills.includes(key) ? 'var(--border)' : 'transparent', color: f.skills.includes(key) ? 'var(--text-primary)' : 'var(--text-dim)', fontSize: '12px', cursor: 'pointer' }}>
                    {s}
                  </button>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '6px' }}>
              {(f.customSkills?.Other||[]).map(s => (
                <span key={s} style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: '6px', padding: '2px 8px', fontSize: '12px', color: 'var(--text-body)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {s}<button type="button" onClick={() => removeCustomSkill('Other', s)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: RED, fontSize: '12px', padding: 0 }}>✕</button>
                </span>
              ))}
            </div>
            {(f.customSkills?.Other||[]).length < 10 && (
              <div style={{ display: 'flex', gap: '6px' }}>
                <input style={{ ...I, flex: 1 }} placeholder="Add custom skill…" value={customInputs.Other||''} onChange={e => setCustomInputs(p => ({ ...p, Other: e.target.value }))} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustomSkill('Other'))} />
                <button type="button" onClick={() => addCustomSkill('Other')} style={{ padding: '6px 12px', background: 'var(--border)', color: 'var(--text-body)', border: '1px solid var(--border-strong)', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>+</button>
              </div>
            )}
          </div>

          {/* Languages */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={L}>Languages</label>
              <button type="button" onClick={addLang} style={{ background: 'none', border: 'none', color: RED, cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>+ Add</button>
            </div>
            {(f.languagesDetail||[]).map((l,i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                <input style={I} placeholder="Language" value={l.language} onChange={e => setLang(i,'language',e.target.value)} />
                <select style={S} value={l.level} onChange={e => setLang(i,'level',e.target.value)}>{LANG_LEVELS.map(lv => <option key={lv}>{lv}</option>)}</select>
                <button type="button" onClick={() => removeLang(i)} style={{ background: 'none', border: 'none', color: RED, cursor: 'pointer', fontSize: '16px' }}>✕</button>
              </div>
            ))}
          </div>

          {/* Department levels — мультивибір (Crew chief / Technician / Engineer / Operator на департамент) */}
          {f.departments.length > 0 && (
            <div>
              <label style={L}>Level per department</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '6px' }}>
                {f.departments.map(d => (
                  <div key={d}>
                    <span style={{ color: DEPT_COLOR[d], fontSize: '12px', fontWeight: 700, display: 'block', marginBottom: '4px' }}>{d}</span>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {DEPT_LEVELS.map(lv => {
                        const active = (f.departmentLevels?.[d] || []).includes(lv);
                        return (
                          <button type="button" key={lv} onClick={() => toggleDeptLevel(d, lv)}
                            style={{ padding: '4px 10px', borderRadius: '6px', border: `1.5px solid ${active ? 'var(--text-body)' : 'var(--border-strong)'}`, background: active ? 'var(--border)' : 'transparent', color: active ? 'var(--text-primary)' : 'var(--text-dim)', fontSize: '12px', cursor: 'pointer' }}>
                            {active ? '✓ ' : ''}{lv}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Additional skills — вільний список (control desks, обладнання, ліцензії тощо) */}
          <div>
            <label style={L}>Additional Skills</label>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', margin: '6px 0' }}>
              {freeSkills.map(s => (
                <span key={s} style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: '6px', padding: '2px 8px', fontSize: '12px', color: 'var(--text-body)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {s}<button type="button" onClick={() => removeFreeSkill(s)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: RED, fontSize: '12px', padding: 0 }}>✕</button>
                </span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <input style={{ ...I, flex: 1 }} placeholder="e.g. GrandMA3, Avolites…" value={newSkillInput} onChange={e => setNewSkillInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addFreeSkill())} />
              <button type="button" onClick={addFreeSkill} style={{ padding: '6px 12px', background: 'var(--border)', color: 'var(--text-body)', border: '1px solid var(--border-strong)', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>+</button>
            </div>
          </div>

          {/* Freelancer's own comment (заповнюється фрілансером у себе в кабінеті, тут — лише перегляд/редагування) */}
          <div>
            <label style={L}>Freelancer's Comment</label>
            <textarea style={{ ...I, resize: 'vertical', minHeight: '70px' }} value={f.freelancerComment||''} onChange={e => set('freelancerComment', e.target.value)} placeholder="Written by the freelancer during onboarding…" />
          </div>

          {/* Projects */}
          <div>
            <label style={L}>Projects</label>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '8px' }}>
              {(f.projects||[]).map(p => (
                <span key={p} style={{ background: 'var(--info-bg)', color: 'var(--info-text)', border: '1px solid #3b82f644', borderRadius: '6px', padding: '2px 8px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {p}<button type="button" onClick={() => removeProject(p)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: RED, fontSize: '12px', padding: 0 }}>✕</button>
                </span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <input style={{ ...I, flex: 1 }} placeholder="Add project name…" value={projInput} onChange={e => setProjInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addProject())} />
              <button type="button" onClick={addProject} style={{ padding: '6px 12px', background: 'var(--border)', color: 'var(--text-body)', border: '1px solid var(--border-strong)', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>+</button>
            </div>
          </div>
        </>}

        {tab === 'Documents' && <>

          {/* Google Drive */}
          <div>
            <label style={L}>Google Drive Folder</label>
            <input style={I} value={f.driveLink} onChange={e => set('driveLink', e.target.value)} placeholder="https://drive.google.com/drive/folders/…" />
            {f.driveLink && (
              <a href={f.driveLink} target="_blank" rel="noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '8px', background: '#1a73e8', color: 'var(--text-primary)', borderRadius: '8px', padding: '7px 14px', textDecoration: 'none', fontSize: '13px', fontWeight: 600 }}>
                📁 Open Google Drive
              </a>
            )}
          </div>

          {/* ── Mandatory ─────────────────────────────────────────────────── */}
          <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '1px' }}>Mandatory</div>

          {!freelancerId && (
            <div style={{ color: 'var(--warn-text, #d29922)', fontSize: '11px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: '8px', padding: '8px 10px' }}>
              Save the profile first — document uploads are enabled once the freelancer card exists.
            </div>
          )}

          {/* Passport */}
          <DocSection title="Passport" required>
            <UploadArea accept="image/*,.pdf" onFiles={files => uploadCatFile('passport', 'passportFiles', files)} />
            {docUploading.passport && <div style={{ color: 'var(--text-dim)', fontSize: '11px' }}>Uploading…</div>}
            <FileList files={f.passportFiles||[]} onRemove={id => removeCatFile('passport', 'passportFiles', id)} />
          </DocSection>

          {/* Personal Photo */}
          <DocSection title="Personal Photo" required>
            <UploadArea accept="image/*" multiple={false} onFiles={files => uploadCatFile('photo', 'photoFiles', files)} />
            {docUploading.photo && <div style={{ color: 'var(--text-dim)', fontSize: '11px' }}>Uploading…</div>}
            <FileList files={f.photoFiles||[]} onRemove={id => removeCatFile('photo', 'photoFiles', id)} />
          </DocSection>

          {/* CV */}
          <DocSection title="CV" required>
            <UploadArea accept=".pdf,.doc,.docx" onFiles={files => uploadCatFile('cv', 'cvFiles', files)} />
            {docUploading.cv && <div style={{ color: 'var(--text-dim)', fontSize: '11px' }}>Uploading…</div>}
            <FileList files={f.cvFiles||[]} onRemove={id => removeCatFile('cv', 'cvFiles', id)} />
          </DocSection>

          {/* ── Optional ──────────────────────────────────────────────────── */}
          <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '4px' }}>Optional</div>

          {/* Visas */}
          <div style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: '10px', padding: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-body)' }}>Visas</span>
              <button type="button" onClick={addVisa}
                style={{ background: 'none', border: '1px solid var(--border-strong)', color: 'var(--text-muted)', padding: '3px 10px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>+ Add Visa</button>
            </div>
            {(f.visas||[]).length === 0 && (
              <div style={{ color: 'var(--text-disabled)', fontSize: '12px', textAlign: 'center', padding: '8px 0' }}>No visas added</div>
            )}
            {(f.visas||[]).map((v, i) => (
              <div key={v.id||i} style={{ background: 'var(--bg-deep)', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px', marginBottom: '8px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                  <input style={I} placeholder="Country / Visa type" value={v.country} onChange={e => setVisa(i,'country',e.target.value)} />
                  <input type="date" style={I} value={v.expiry} onChange={e => setVisa(i,'expiry',e.target.value)} />
                  <button type="button" onClick={() => removeVisa(i)} style={{ background: 'none', border: 'none', color: RED, cursor: 'pointer', fontSize: '16px' }}>✕</button>
                </div>
                <UploadArea accept="image/*,.pdf" onFiles={files => addVisaFiles(i, files)} />
                <FileList files={v.files||[]} onRemove={id => removeVisaFile(i, id)} />
              </div>
            ))}
          </div>

          {/* Other Documents */}
          <div style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: '10px', padding: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-body)' }}>Others</span>
              <button type="button" onClick={addOtherDoc}
                style={{ background: 'none', border: '1px solid var(--border-strong)', color: 'var(--text-muted)', padding: '3px 10px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>+ Add Document</button>
            </div>
            {(f.otherDocs||[]).length === 0 && (
              <div style={{ color: 'var(--text-disabled)', fontSize: '12px', textAlign: 'center', padding: '8px 0' }}>No other documents</div>
            )}
            {(f.otherDocs||[]).map((d, i) => (
              <div key={d.id||i} style={{ background: 'var(--bg-deep)', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px', marginBottom: '8px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                  <input style={I} placeholder="Document title" value={d.title} onChange={e => setOtherDoc(i,'title',e.target.value)} />
                  <input type="date" style={{ ...I, color: d.expiry ? 'var(--text-body)' : 'var(--text-disabled)' }} value={d.expiry} onChange={e => setOtherDoc(i,'expiry',e.target.value)} title="Expiry date (optional)" />
                  <button type="button" onClick={() => removeOtherDoc(i)} style={{ background: 'none', border: 'none', color: RED, cursor: 'pointer', fontSize: '16px' }}>✕</button>
                </div>
                {freelancerId
                  ? <UploadArea accept="image/*,.pdf,.doc,.docx" onFiles={files => addOtherDocFiles(i, files)} />
                  : <div style={{ color: 'var(--text-disabled)', fontSize: '11px', padding: '4px 0' }}>Save the profile first, then upload files.</div>}
                {docUploading.other && <div style={{ color: 'var(--text-dim)', fontSize: '11px' }}>Uploading…</div>}
                <FileList files={d.files||[]} onRemove={id => removeOtherDocFile(i, id)} />
              </div>
            ))}
          </div>
        </>}

        {tab === 'Financial' && canFinance && <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label style={L}>Bank Accounts</label>
            <button type="button" onClick={addBankAccount} style={{ background: 'none', border: 'none', color: RED, cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>+ Add bank</button>
          </div>
          {(f.bankAccounts||[]).length === 0 && (
            <div style={{ color: 'var(--text-disabled)', fontSize: '12px', textAlign: 'center', padding: '8px 0' }}>No bank accounts added</div>
          )}
          {(f.bankAccounts||[]).map((acc, i) => {
            const isCustomCurrency = !BANK_CURRENCIES.includes(acc.currency);
            return (
              <div key={i} style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: '10px', padding: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)' }}>Bank {i + 1}</span>
                  <button type="button" onClick={() => removeBankAccount(i)} style={{ background: 'none', border: 'none', color: RED, cursor: 'pointer', fontSize: '16px' }}>✕</button>
                </div>
                <div style={G}>
                  <div><label style={L}>Bank Name</label><input style={I} value={acc.bankName||''} onChange={e => setBankAccountF(i,'bankName',e.target.value)} /></div>
                  <div><label style={L}>Currency</label>
                    <select style={S} value={isCustomCurrency ? '__custom__' : acc.currency} onChange={e => setBankAccountF(i,'currency', e.target.value === '__custom__' ? '' : e.target.value)}>
                      {BANK_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                      <option value="__custom__">Other…</option>
                    </select>
                    {isCustomCurrency && (
                      <input style={{ ...I, marginTop: '6px' }} placeholder="Currency code" maxLength={10} value={acc.currency||''} onChange={e => setBankAccountF(i,'currency', e.target.value.toUpperCase().slice(0, 10))} />
                    )}
                  </div>
                </div>
                <div><label style={L}>Account Number / IBAN</label><input style={I} value={acc.accountNumber||''} onChange={e => setBankAccountF(i,'accountNumber',e.target.value)} /></div>
                <div style={G}>
                  <div><label style={L}>SWIFT / BIC</label><input style={I} value={acc.swift||''} onChange={e => setBankAccountF(i,'swift',e.target.value)} /></div>
                  <div><label style={L}>Account Holder</label><input style={I} value={acc.accountHolderName||''} onChange={e => setBankAccountF(i,'accountHolderName',e.target.value)} /></div>
                </div>
              </div>
            );
          })}
        </>}

        {tab === 'Comments' && (
          <div>
            <label style={L}>Admin Comments / Notes</label>
            <textarea style={{ ...I, resize: 'vertical', minHeight: '120px' }} value={f.comment||''} onChange={e => set('comment',e.target.value)} placeholder="Internal notes…" />
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', paddingTop: '10px', borderTop: '1px solid var(--border)', marginTop: '4px' }}>
          <button type="button" onClick={onCancel}
            style={{ flex: 1, padding: '10px', border: '1px solid var(--border-strong)', borderRadius: '8px', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>
            Cancel
          </button>
          <button type="submit"
            style={{ flex: 2, padding: '10px', border: 'none', borderRadius: '8px', background: RED, color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 700, fontSize: '13px' }}>
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Side panel sub-components ────────────────────────────────────────────────
export function PSection({ title, children }) {
  return (
    <div>
      <p style={{ margin: '0 0 10px', fontSize: '10px', fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '1px' }}>{title}</p>
      {children}
    </div>
  );
}
export function PRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginBottom: '5px' }}>
      <span style={{ color: 'var(--text-dim)', fontSize: '12px', flexShrink: 0 }}>{label}</span>
      <span style={{ color: 'var(--text-body)', fontSize: '12px', fontWeight: 500, textAlign: 'right', wordBreak: 'break-all' }}>{value}</span>
    </div>
  );
}

// ─── Style helpers ────────────────────────────────────────────────────────────
const TH = {
  padding: '10px 14px', textAlign: 'left', color: 'var(--text-dim)',
  fontSize: '11px', fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '1px', borderBottom: '1px solid var(--border)',
  whiteSpace: 'nowrap', background: 'var(--bg-card)',
};
const TD = {
  padding: '11px 14px', color: 'var(--text-body)', fontSize: '13px',
  borderBottom: '1px solid var(--bg-hover)', verticalAlign: 'middle',
};
